// ===================================================================
// 📋 GERENCIADOR DE PROCESSOS - EXEMPLO DE IMPLEMENTAÇÃO
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { formatDate, formatCurrency, validateFields } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { ProcessQuotaConfigModal } from '../../components/admin/ProcessQuotaConfigModal';
import { sortColumnIndicator } from '../../utils/adminTableSort';
import {
  getSelectionModeLabel,
  normalizeSelectionMode,
  IMPLEMENTED_SELECTION_MODES,
} from '../../utils/selectionMode';
import {
  processDefinitiveAnswerKeyReady,
  processPreliminaryAnswerKeyReady,
} from '../../utils/examAnswerKeyPolicy';

const initialProcessSort = { key: null, dir: 'asc' };

const nextProcessSort = (prev, key) => {
  if (prev.key !== key) return { key, dir: 'asc' };
  return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
};

/** Rótulos de tipo de folha (A, B, C1 …), alinhado ao backend. */
function parseGabaritoVariantLabels(raw) {
  const parts = String(raw || '')
    .split(/[\s,;]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  const uniq = [];
  for (const p of parts) {
    const lab = p.replace(/[^A-Z0-9]/g, '').slice(0, 8);
    if (lab && !uniq.includes(lab)) uniq.push(lab);
  }
  return uniq.length ? uniq.slice(0, 12) : ['A'];
}

function gabaritoTotalQuestionsFromRows(rows) {
  return (rows || []).reduce(
    (s, r) => s + (parseInt(String(r?.question_count ?? '').trim(), 10) || 0),
    0,
  );
}

function gabaritoOmrLetterOptions(numAltStr) {
  const n = Math.max(2, Math.min(6, parseInt(String(numAltStr || '5'), 10) || 5));
  return 'ABCDEF'.slice(0, n).split('');
}

/** Símbolos de anulação / exclusão (preliminar e definitivo). */
const GABARITO_ANULACAO_SYMBOLS = [
  {
    value: '*',
    label: '*  todos',
    title:
      'Anulação com pontuação: todos recebem o ponto nesta questão (a correção tratará como acerto pleno, qualquer marcação ou em branco).',
  },
  {
    value: '#',
    label: '#  exc.',
    title:
      'Questão desconsiderada na nota: não soma nem penaliza neste item (excluída do cálculo da pontuação).',
  },
];

/** Converte string salva no backend em uma linha por questão (comprimento n). */
function gabaritoAnswerStringToRowArray(str, n) {
  const s = String(str || '').replace(/\s+/g, '').toUpperCase();
  const arr = [];
  for (let i = 0; i < n; i += 1) {
    arr.push(i < s.length ? s[i] : '');
  }
  return arr;
}

function gabaritoLabelsFromProcess(p) {
  if (Array.isArray(p?.exam_key_variant_labels) && p.exam_key_variant_labels.length) {
    return p.exam_key_variant_labels.map((x) => String(x).toUpperCase());
  }
  return ['A'];
}

function gabaritoResizeGridRows(prev, labels, n) {
  const next = {};
  labels.forEach((lab) => {
    const old = (prev && prev[lab]) || [];
    const row = old.slice(0, n);
    while (row.length < n) row.push('');
    next[lab] = row;
  });
  return next;
}

/** Se todas as n posições têm letra, retorna string; senão null. n===0 retorna null. */
function gabaritoGridToFilledString(gridArr, n) {
  if (!n || n <= 0) return null;
  const a = gridArr || [];
  let s = '';
  for (let i = 0; i < n; i += 1) {
    const c = String(a[i] || '').toUpperCase().slice(0, 1);
    if (!c) return null;
    s += c;
  }
  return s;
}

/** Atualiza uma célula; retorna o novo estado. */
function gabaritoGridWithCell(prev, lab, idx, value, nq) {
  const row = [...((prev && prev[lab]) || [])];
  while (row.length < nq) row.push('');
  row[idx] = value;
  return { ...(prev || {}), [lab]: row };
}

// Função para gerar código único do processo
const generateProcessCode = (name) => {
  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, '0');
  const timestamp = Date.now().toString().slice(-4);
  const nameCode = name.substring(0, 3).toUpperCase().replace(/\s/g, '');
  return `PS${year}${month}${nameCode}${timestamp}`;
};

function normalizeCityStateLabel(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\-/,;]+/g, ' ')
    .replace(/[^a-zA-Z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function isCityStateLabelValid(value) {
  return /^[A-Z0-9 ]+ [A-Z]{2}$/.test(String(value || ''));
}

const processFormPanel = {
  marginTop: '20px',
  padding: '16px 18px',
  backgroundColor: '#F9FAFB',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
};
const processFormPanelTitle = {
  margin: '0 0 14px 0',
  fontSize: '15px',
  fontWeight: 600,
  color: '#111827',
};

export const ProcessManager = React.memo(({ user }) => {
  const { refreshProcesses } = useAdminProcess();
  const [processes, setProcesses] = useState([]);
  const [tableSort, setTableSort] = useState(initialProcessSort);
  const [loading, setLoading] = useState(true);
  const [usableSelectionModes, setUsableSelectionModes] = useState(IMPLEMENTED_SELECTION_MODES);
  const [showModal, setShowModal] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cadunicoReady, setCadunicoReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiClient.getConfig();
        if (!cancelled) setCadunicoReady(Boolean(cfg?.cadunico_ready));
      } catch {
        if (!cancelled) setCadunicoReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    questionnaire_id: '',
    start_date: '',
    end_date: '',
    selection_mode: 'analise_curricular',
    has_fee: false,
    enable_exemption_request: false,
    exemption_cadunico_consulta: false,
    fee_amount: 0,
    fee_payment_start: '',
    fee_payment_end: '',
    payment_method_id: '',
    use_quotas: false,
    allow_multiple_applications: false,
    is_active: true,
    exemption_start: '',
    exemption_end: '',
    exemption_review_start: '',
    exemption_review_end: '',
    registration_appeal_start: '',
    registration_appeal_end: '',
    answer_key_appeal_start: '',
    answer_key_appeal_end: '',
    result_appeal_start: '',
    result_appeal_end: '',
    enable_edital_challenge: false,
    edital_challenge_start: '',
    edital_challenge_end: '',
    edital_challenge_response_until: '',
    show_candidate_average: true,
    show_appeal_response_to_candidates: false,
    use_special_attendance_specialized: false,
    use_special_attendance_specific: false,
    special_attendance_start: '',
    special_attendance_end: '',
    special_attendance_appeal_start: '',
    special_attendance_appeal_end: '',
    special_attendance_type_ids: [],
    allow_change_quota_during_registration: true,
    allow_change_course_during_registration: true,
    release_grades_automatic_scanned: false,
    release_grades_manual: false,
    allow_resend_exemption_in_exemption_period: true,
    allow_resend_special_attendance_in_request_period: true,
    allow_resend_curricular_in_registration_period: true,
    exam_uses_answer_sheet: false,
    exam_uses_essay: false,
    essay_sheet_num_lines: 28,
    exam_essay_correction_multiplier: 4,
    exam_disqualify_on_zero_objective_area: true,
    exam_essay_max_score: 40,
    exam_objective_max_points: 60,
    exam_objective_score_mode: 'weighted_average',
    exam_allocation_policy: 'course_location',
    exam_shortage_policy: 'mark_pending',
    exam_card_release_start: '',
    exam_card_release_end: '',
    exam_day_date: '',
    exam_day_gate_open: '',
    exam_day_gate_close: '',
    exam_day_exam_start: '',
    exam_day_min_stay_until: '',
    exam_day_exam_end: '',
    exam_day_extra_instructions: '',
  });
  const [specialAttendanceTypes, setSpecialAttendanceTypes] = useState([]);
  const [error, setError] = useState('');
  const [quotaConfigProcess, setQuotaConfigProcess] = useState(null);
  const [docsModalProcess, setDocsModalProcess] = useState(null);
  const [processDocuments, setProcessDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docFormError, setDocFormError] = useState('');
  const [docFormData, setDocFormData] = useState({ name: '', published_at: '' });
  const [docUploading, setDocUploading] = useState(false);
  const [scheduleModalProcess, setScheduleModalProcess] = useState(null);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleFormData, setScheduleFormData] = useState({ label: '', date_type: 'period', single_date: '', start_date: '', end_date: '' });
  const [scheduleFormError, setScheduleFormError] = useState('');
  const [editingScheduleItem, setEditingScheduleItem] = useState(null);
  const sortedProcesses = useMemo(() => {
    const list = [...processes];
    if (!tableSort.key) return list;

    const dir = tableSort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (tableSort.key === 'name') {
        const byName = String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
        if (byName !== 0) return byName * dir;
        return String(a.selection_mode || '').localeCompare(String(b.selection_mode || ''), 'pt-BR') * dir;
      }
      const byModality = String(a.selection_mode || '').localeCompare(String(b.selection_mode || ''), 'pt-BR');
      if (byModality !== 0) return byModality * dir;
      return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR') * dir;
    });
    return list;
  }, [processes, tableSort]);
  const [examVenuesModalProcess, setExamVenuesModalProcess] = useState(null);
  const [examVenuesList, setExamVenuesList] = useState([]);
  const [examVenuesLoading, setExamVenuesLoading] = useState(false);
  const [venueForm, setVenueForm] = useState({ name: '', street: '', neighborhood: '', city_state: '' });
  const [roomForm, setRoomForm] = useState({ venueId: '', name: '', building: '', notes: '', capacity: '' });
  const [gabaritoModeloModalProcess, setGabaritoModeloModalProcess] = useState(null);
  const [gabaritoExamRows, setGabaritoExamRows] = useState([]);
  const [gabaritoNumAlt, setGabaritoNumAlt] = useState('5');
  const [gabaritoVariantsStr, setGabaritoVariantsStr] = useState('A');
  const [gabaritoPrelimGrid, setGabaritoPrelimGrid] = useState({});
  const [gabaritoDefGrid, setGabaritoDefGrid] = useState({});
  const [gabaritoHistory, setGabaritoHistory] = useState([]);
  const [gabaritoDraftSaving, setGabaritoDraftSaving] = useState(false);
  const [gabaritoDraftFeedback, setGabaritoDraftFeedback] = useState(null);
  const gabaritoSkipResizeAfterLoadRef = useRef(false);
  const gabaritoPrelimGridRef = useRef({});
  const gabaritoDefGridRef = useRef({});
  const [essayLinesModalOpen, setEssayLinesModalOpen] = useState(false);
  const [essayLinesDraft, setEssayLinesDraft] = useState('28');
  const { addNotification } = useNotifications();

  const objectivePartMaxPointsHint = useMemo(() => {
    const w = editingProcess?.exam_area_weights;
    if (Array.isArray(w) && w.length > 0) {
      const n = gabaritoTotalQuestionsFromRows(w);
      if (n > 0) return { fromGabarito: true, n };
    }
    const n = Math.max(1, Math.min(500, parseInt(String(formData.exam_objective_max_points), 10) || 60));
    return { fromGabarito: false, n };
  }, [editingProcess?.id, editingProcess?.exam_area_weights, formData.exam_objective_max_points]);

  const loadExamVenues = useCallback(async (processId) => {
    if (!processId) return;
    setExamVenuesLoading(true);
    try {
      const data = await apiClient.getExamVenues(processId);
      setExamVenuesList(Array.isArray(data) ? data : []);
    } catch {
      setExamVenuesList([]);
    } finally {
      setExamVenuesLoading(false);
    }
  }, []);

  useEffect(() => {
    apiClient.getConfig().then((data) => {
      if (Array.isArray(data?.usable_selection_modes) && data.usable_selection_modes.length) {
        setUsableSelectionModes(data.usable_selection_modes);
      }
    }).catch(() => {});
  }, []);

  const selectionModeOptions = useMemo(() => {
    const ids = [...usableSelectionModes];
    const cur = normalizeSelectionMode(formData.selection_mode);
    if (cur && !ids.includes(cur)) ids.unshift(cur);
    return ids;
  }, [usableSelectionModes, formData.selection_mode]);

  useEffect(() => {
    if (examVenuesModalProcess?.id) {
      loadExamVenues(examVenuesModalProcess.id);
    }
  }, [examVenuesModalProcess?.id, loadExamVenues]);

  useEffect(() => {
    const p = gabaritoModeloModalProcess;
    if (!p?.id) {
      setGabaritoExamRows([]);
      setGabaritoVariantsStr('A');
      setGabaritoNumAlt('5');
      setGabaritoPrelimGrid({});
      setGabaritoDefGrid({});
      gabaritoPrelimGridRef.current = {};
      gabaritoDefGridRef.current = {};
      gabaritoSkipResizeAfterLoadRef.current = false;
      return;
    }
    const src = Array.isArray(p.exam_area_weights) ? p.exam_area_weights : [];
    const rows = src.length
      ? src.map((r) => ({
          area: (r && (r.area || r.name)) || '',
          question_count: r?.question_count ?? r?.questions ?? '',
          weight: r?.weight ?? 1,
        }))
      : [{ area: '', question_count: '', weight: 1 }];
    setGabaritoExamRows(rows);
    const labels = gabaritoLabelsFromProcess(p);
    setGabaritoVariantsStr(labels.join(', '));
    setGabaritoNumAlt(String(p.exam_num_alternatives ?? 5));
    const nq = gabaritoTotalQuestionsFromRows(rows);
    const pre = typeof p.exam_answer_key_preliminary === 'object' && p.exam_answer_key_preliminary
      ? p.exam_answer_key_preliminary
      : {};
    const def = typeof p.exam_answer_key_definitive === 'object' && p.exam_answer_key_definitive
      ? p.exam_answer_key_definitive
      : {};
    const preGrid = {};
    const defGrid = {};
    labels.forEach((lab) => {
      preGrid[lab] = gabaritoAnswerStringToRowArray(pre[lab] != null ? String(pre[lab]) : '', nq);
      defGrid[lab] = gabaritoAnswerStringToRowArray(def[lab] != null ? String(def[lab]) : '', nq);
    });
    setGabaritoPrelimGrid(preGrid);
    setGabaritoDefGrid(defGrid);
    gabaritoPrelimGridRef.current = preGrid;
    gabaritoDefGridRef.current = defGrid;
    gabaritoSkipResizeAfterLoadRef.current = true;
    setGabaritoDraftFeedback(null);
    setGabaritoDraftSaving(false);
  }, [gabaritoModeloModalProcess]);

  const gabaritoParsedLabels = useMemo(
    () => parseGabaritoVariantLabels(gabaritoVariantsStr),
    [gabaritoVariantsStr],
  );

  const gabaritoQuestionBlocks = useMemo(() => {
    const blocks = [];
    let start = 1;
    for (const r of gabaritoExamRows || []) {
      const cnt = parseInt(String(r?.question_count ?? '').trim(), 10) || 0;
      if (cnt <= 0) continue;
      const area = (r.area || '').trim() || '—';
      blocks.push({
        area,
        start,
        end: start + cnt - 1,
        count: cnt,
      });
      start += cnt;
    }
    return blocks;
  }, [gabaritoExamRows]);

  useEffect(() => {
    if (!gabaritoModeloModalProcess?.id) return;
    if (gabaritoSkipResizeAfterLoadRef.current) {
      gabaritoSkipResizeAfterLoadRef.current = false;
      return;
    }
    const nq = gabaritoTotalQuestionsFromRows(gabaritoExamRows);
    const labels = gabaritoParsedLabels;
    setGabaritoPrelimGrid((prev) => {
      const next = gabaritoResizeGridRows(prev, labels, nq);
      gabaritoPrelimGridRef.current = next;
      return next;
    });
    setGabaritoDefGrid((prev) => {
      const next = gabaritoResizeGridRows(prev, labels, nq);
      gabaritoDefGridRef.current = next;
      return next;
    });
  }, [gabaritoExamRows, gabaritoParsedLabels, gabaritoModeloModalProcess?.id]);

  useEffect(() => {
    const id = gabaritoModeloModalProcess?.id;
    if (!id) {
      setGabaritoHistory([]);
      return;
    }
    let cancelled = false;
    apiClient.getProcessAnswerKeyHistory(id).then((rows) => {
      if (!cancelled) setGabaritoHistory(Array.isArray(rows) ? rows : []);
    }).catch(() => {
      if (!cancelled) setGabaritoHistory([]);
    });
    return () => { cancelled = true; };
  }, [gabaritoModeloModalProcess?.id]);

  const loadProcesses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getProcesses(true);
      const arr = Array.isArray(data) ? data : [];
      setProcesses(arr);
      return arr;
    } catch (err) {
      setError('Erro ao carregar processos');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQuestionnaires = useCallback(async () => {
    try {
      const data = await apiClient.getQuestionnaires(true);
      setQuestionnaires(Array.isArray(data) ? data : []);
    } catch {
      setQuestionnaires([]);
    }
  }, []);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  useEffect(() => {
    loadQuestionnaires();
  }, [loadQuestionnaires]);

  const loadPaymentMethods = useCallback(async () => {
    try {
      const data = await apiClient.getPaymentMethods();
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch {
      setPaymentMethods([]);
    }
  }, []);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  useEffect(() => {
    if (!showModal || formData.selection_mode !== 'prova') {
      setSpecialAttendanceTypes([]);
      return;
    }
    let cancelled = false;
    apiClient.getSpecialAttendanceTypes({ activeOnly: true }).then((data) => {
      if (!cancelled) setSpecialAttendanceTypes(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (!cancelled) setSpecialAttendanceTypes([]);
    });
    return () => { cancelled = true; };
  }, [showModal, formData.selection_mode]);

  const loadProcessDocuments = useCallback(async (processId) => {
    if (!processId) return;
    try {
      setLoadingDocs(true);
      const data = await apiClient.getProcessDocuments(processId);
      setProcessDocuments(Array.isArray(data) ? data : []);
    } catch {
      setProcessDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    if (docsModalProcess?.id) loadProcessDocuments(docsModalProcess.id);
  }, [docsModalProcess?.id, loadProcessDocuments]);

  const loadProcessSchedule = useCallback(async (processId) => {
    if (!processId) return;
    try {
      setLoadingSchedule(true);
      const data = await apiClient.getProcessSchedule(processId);
      setScheduleItems(Array.isArray(data) ? data : []);
    } catch {
      setScheduleItems([]);
    } finally {
      setLoadingSchedule(false);
    }
  }, []);

  useEffect(() => {
    if (scheduleModalProcess?.id) loadProcessSchedule(scheduleModalProcess.id);
  }, [scheduleModalProcess?.id, loadProcessSchedule]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const next = {
        ...prev,
        [field]: value,
        ...(field === 'name' && value.length >= 3 ? { code: generateProcessCode(value) } : {}),
      };
      if (field === 'has_fee' && !value) {
        next.allow_resend_exemption_in_exemption_period = false;
      }
      if (field === 'use_quotas' && !value) {
        next.allow_change_quota_during_registration = false;
      }
      return next;
    });
    if (error) setError('');
  }, [error]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Validar apenas campos obrigatórios
    const requiredFields = {
      name: formData.name,
      start_date: formData.start_date,
      end_date: formData.end_date
    };
    
    // Adicionar campos de isenção apenas quando o processo permitir pedido de isenção
    if (formData.has_fee && formData.enable_exemption_request) {
      requiredFields.exemption_start = formData.exemption_start;
      requiredFields.exemption_end = formData.exemption_end;
      requiredFields.exemption_review_start = formData.exemption_review_start;
      requiredFields.exemption_review_end = formData.exemption_review_end;
    }
    
    if (!validateFields(requiredFields)) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);

      // Validar períodos de recurso não sobrepostos
      const parseToDate = (s) => {
        if (!s || !s.trim()) return null;
        const parts = s.includes('-') ? s.split('-') : s.split('/');
        if (parts.length !== 3) return null;
        let y, m, d;
        if (s.includes('-')) {
          y = parseInt(parts[0], 10); m = parseInt(parts[1], 10) - 1; d = parseInt(parts[2], 10);
        } else {
          d = parseInt(parts[0], 10); m = parseInt(parts[1], 10) - 1; y = parseInt(parts[2], 10);
        }
        const dt = new Date(y, m, d);
        return isNaN(dt.getTime()) ? null : dt;
      };
      const periods = [
        { name: 'Recurso de Isenção', start: formData.exemption_review_start, end: formData.exemption_review_end },
        { name: 'Recurso de Lista de Inscritos', start: formData.registration_appeal_start, end: formData.registration_appeal_end },
      ];
      if (formData.selection_mode === 'prova' && (formData.use_special_attendance_specialized || formData.use_special_attendance_specific)) {
        periods.push({
          name: 'Recurso contra o Resultado preliminar dos pedidos de atendimento especializado/específico',
          start: formData.special_attendance_appeal_start,
          end: formData.special_attendance_appeal_end,
        });
      }
      if (formData.selection_mode === 'prova') {
        periods.push({ name: 'Recurso contra o Gabarito', start: formData.answer_key_appeal_start, end: formData.answer_key_appeal_end });
      }
      periods.push({ name: 'Recurso de Resultado Preliminar', start: formData.result_appeal_start, end: formData.result_appeal_end });
      const periodsParsed = periods
        .filter(p => p.start && p.end)
        .map(p => ({ ...p, d1: parseToDate(p.start), d2: parseToDate(p.end) }))
        .filter(p => p.d1 && p.d2);
      for (let i = 0; i < periodsParsed.length; i++) {
        for (let j = i + 1; j < periodsParsed.length; j++) {
          const a = periodsParsed[i], b = periodsParsed[j];
          if (a.d1 <= b.d2 && b.d1 <= a.d2) {
            setError(`Períodos não podem se sobrepor: "${a.name}" e "${b.name}"`);
            setLoading(false);
            return;
          }
        }
      }

      // Converter datas do formato YYYY-MM-DD (HTML) para DD/MM/YYYY (Brasil)
      const convertDateToBrazilianFormat = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      };

      const hasFee = Boolean(formData.has_fee);
      const hasExemptionRequest = hasFee && Boolean(formData.enable_exemption_request);

      const processData = {
        name: formData.name,
        description: formData.description,
        code: formData.code,
        start_date: convertDateToBrazilianFormat(formData.start_date),
        end_date: convertDateToBrazilianFormat(formData.end_date),
        selection_mode: formData.selection_mode,
        has_fee: hasFee,
        enable_exemption_request: hasExemptionRequest,
        exemption_cadunico_consulta: hasExemptionRequest ? Boolean(formData.exemption_cadunico_consulta) : false,
        fee_amount: parseFloat(formData.fee_amount) || 0,
        fee_payment_start: convertDateToBrazilianFormat(formData.fee_payment_start),
        fee_payment_end: convertDateToBrazilianFormat(formData.fee_payment_end),
        payment_method_id: formData.payment_method_id ? parseInt(formData.payment_method_id, 10) : null,
        use_quotas: Boolean(formData.use_quotas),
        allow_multiple_applications: Boolean(formData.allow_multiple_applications),
        is_active: Boolean(formData.is_active),
        exemption_start: hasExemptionRequest ? convertDateToBrazilianFormat(formData.exemption_start) : null,
        exemption_end: hasExemptionRequest ? convertDateToBrazilianFormat(formData.exemption_end) : null,
        exemption_appeal_start: hasExemptionRequest ? convertDateToBrazilianFormat(formData.exemption_review_start) : null,
        exemption_appeal_end: hasExemptionRequest ? convertDateToBrazilianFormat(formData.exemption_review_end) : null,
        exemption_review_start: hasExemptionRequest ? convertDateToBrazilianFormat(formData.exemption_review_start) : null,
        exemption_review_end: hasExemptionRequest ? convertDateToBrazilianFormat(formData.exemption_review_end) : null,
        registration_appeal_start: convertDateToBrazilianFormat(formData.registration_appeal_start),
        registration_appeal_end: convertDateToBrazilianFormat(formData.registration_appeal_end),
        answer_key_appeal_start: convertDateToBrazilianFormat(formData.answer_key_appeal_start),
        answer_key_appeal_end: convertDateToBrazilianFormat(formData.answer_key_appeal_end),
        result_appeal_start: convertDateToBrazilianFormat(formData.result_appeal_start),
        result_appeal_end: convertDateToBrazilianFormat(formData.result_appeal_end),
        enable_edital_challenge: Boolean(formData.enable_edital_challenge),
        edital_challenge_start: formData.enable_edital_challenge
          ? convertDateToBrazilianFormat(formData.edital_challenge_start)
          : null,
        edital_challenge_end: formData.enable_edital_challenge
          ? convertDateToBrazilianFormat(formData.edital_challenge_end)
          : null,
        edital_challenge_response_until: formData.enable_edital_challenge
          ? convertDateToBrazilianFormat(formData.edital_challenge_response_until)
          : null,
        questionnaire_id: formData.questionnaire_id ? parseInt(formData.questionnaire_id, 10) : null,
        show_candidate_average: Boolean(formData.show_candidate_average),
        show_appeal_response_to_candidates: Boolean(formData.show_appeal_response_to_candidates),
        use_special_attendance_specialized: Boolean(formData.use_special_attendance_specialized),
        use_special_attendance_specific: Boolean(formData.use_special_attendance_specific),
        special_attendance_start: convertDateToBrazilianFormat(formData.special_attendance_start),
        special_attendance_end: convertDateToBrazilianFormat(formData.special_attendance_end),
        special_attendance_appeal_start: convertDateToBrazilianFormat(formData.special_attendance_appeal_start),
        special_attendance_appeal_end: convertDateToBrazilianFormat(formData.special_attendance_appeal_end),
        special_attendance_type_ids: (formData.special_attendance_type_ids || []).map((x) => Number(x)).filter((n) => !Number.isNaN(n)),
        allow_change_quota_during_registration: Boolean(formData.allow_change_quota_during_registration),
        allow_change_course_during_registration: Boolean(formData.allow_change_course_during_registration),
        release_grades_automatic_scanned: Boolean(formData.release_grades_automatic_scanned),
        release_grades_manual: Boolean(formData.release_grades_manual),
        allow_resend_exemption_in_exemption_period: Boolean(formData.allow_resend_exemption_in_exemption_period),
        allow_resend_special_attendance_in_request_period: Boolean(formData.allow_resend_special_attendance_in_request_period),
        allow_resend_curricular_in_registration_period: Boolean(formData.allow_resend_curricular_in_registration_period),
        exam_uses_answer_sheet: Boolean(formData.exam_uses_answer_sheet),
        exam_objective_score_mode:
          formData.exam_objective_score_mode === 'weighted_sum' ? 'weighted_sum' : 'weighted_average',
        exam_uses_essay: Boolean(formData.exam_uses_essay),
        essay_sheet_num_lines: Math.max(
          10,
          Math.min(30, parseInt(String(formData.essay_sheet_num_lines), 10) || 28)
        ),
        exam_essay_correction_multiplier: Math.max(
          1,
          Math.min(20, parseInt(String(formData.exam_essay_correction_multiplier), 10) || 4)
        ),
        exam_disqualify_on_zero_objective_area: Boolean(formData.exam_disqualify_on_zero_objective_area),
        exam_essay_max_score: Math.max(
          1,
          Math.min(500, parseInt(String(formData.exam_essay_max_score), 10) || 40)
        ),
        exam_allocation_policy: formData.exam_allocation_policy || 'course_location',
        exam_shortage_policy: formData.exam_shortage_policy || 'mark_pending',
        exam_card_release_start: formData.exam_card_release_start || null,
        exam_card_release_end: formData.exam_card_release_end || null,
        exam_day_date: formData.exam_day_date || null,
        exam_day_gate_open: formData.exam_day_gate_open || null,
        exam_day_gate_close: formData.exam_day_gate_close || null,
        exam_day_exam_start: formData.exam_day_exam_start || null,
        exam_day_min_stay_until: formData.exam_day_min_stay_until || null,
        exam_day_exam_end: formData.exam_day_exam_end || null,
        exam_day_extra_instructions: formData.exam_day_extra_instructions || null,
      };

      console.log('🔄 Datas convertidas:', {
        original: formData.start_date,
        convertida: processData.start_date
      });
      
      if (editingProcess) {
        const { error: updateError } = await apiClient.updateProcess(editingProcess.id, processData);
        if (updateError) {
          setError(updateError);
        } else {
          console.log('✅ Processo atualizado com sucesso:', processData);
          addNotification('Sucesso', 'Processo atualizado com sucesso', 'success');
          setShowModal(false);
          setEditingProcess(null);
          loadProcesses();
          refreshProcesses();
        }
      } else {
        const { error: createError } = await apiClient.createProcess(processData);
        if (createError) {
          setError(createError);
        } else {
          console.log('✅ Processo criado com sucesso:', processData);
          console.log('🔍 Verificando se processo foi salvo no mock data...');
          addNotification('Sucesso', 'Processo criado com sucesso', 'success');
          setShowModal(false);
          loadProcesses();
          refreshProcesses();
        }
      }
    } catch (err) {
      setError('Erro ao salvar processo');
    } finally {
      setLoading(false);
    }
  }, [formData, editingProcess, loadProcesses, addNotification]);

  const handleEdit = useCallback((process) => {
    setEditingProcess(process);
    
    // Converter datas do formato DD/MM/YYYY (Brasil) para YYYY-MM-DD (HTML)
    const convertDateToHTMLFormat = (dateString) => {
      if (!dateString) return '';
      if (dateString.includes('-')) {
        // Se está no formato YYYY-MM-DD, converter para DD/MM/YYYY primeiro
        const [year, month, day] = dateString.split('-');
        const brazilianDate = `${day}/${month}/${year}`;
        // Agora converter para HTML
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      // Se já está no formato DD/MM/YYYY, converter para YYYY-MM-DD
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    setFormData({
      name: process.name || '',
      description: process.description || '',
      code: process.code || '',
      start_date: convertDateToHTMLFormat(process.start_date),
      end_date: convertDateToHTMLFormat(process.end_date),
      selection_mode: normalizeSelectionMode(process.selection_mode),
      has_fee: Boolean(process.has_fee),
      enable_exemption_request: Boolean(process.exemption_start || process.exemption_end || process.exemption_appeal_start || process.exemption_appeal_end),
      exemption_cadunico_consulta: Boolean(process.exemption_cadunico_consulta),
      fee_amount: parseFloat(process.fee_amount) || 0,
      fee_payment_start: convertDateToHTMLFormat(process.fee_payment_start),
      fee_payment_end: convertDateToHTMLFormat(process.fee_payment_end),
      payment_method_id: process.payment_method_id ? String(process.payment_method_id) : '',
      use_quotas: Boolean(process.use_quotas),
      allow_multiple_applications: Boolean(process.allow_multiple_applications),
      is_active: process.is_active !== false,
      exemption_start: convertDateToHTMLFormat(process.exemption_start),
      exemption_end: convertDateToHTMLFormat(process.exemption_end),
      exemption_review_start: convertDateToHTMLFormat(process.exemption_appeal_start),
      exemption_review_end: convertDateToHTMLFormat(process.exemption_appeal_end),
      registration_appeal_start: convertDateToHTMLFormat(process.registration_appeal_start),
      registration_appeal_end: convertDateToHTMLFormat(process.registration_appeal_end),
      answer_key_appeal_start: convertDateToHTMLFormat(process.answer_key_appeal_start),
      answer_key_appeal_end: convertDateToHTMLFormat(process.answer_key_appeal_end),
      result_appeal_start: convertDateToHTMLFormat(process.result_appeal_start),
      result_appeal_end: convertDateToHTMLFormat(process.result_appeal_end),
      enable_edital_challenge: Boolean(process.enable_edital_challenge),
      edital_challenge_start: convertDateToHTMLFormat(process.edital_challenge_start),
      edital_challenge_end: convertDateToHTMLFormat(process.edital_challenge_end),
      edital_challenge_response_until: convertDateToHTMLFormat(process.edital_challenge_response_until),
      questionnaire_id: process.questionnaire_id ? String(process.questionnaire_id) : '',
      show_candidate_average: process.show_candidate_average !== false,
      show_appeal_response_to_candidates: Boolean(process.show_appeal_response_to_candidates),
      use_special_attendance_specialized: Boolean(process.use_special_attendance_specialized),
      use_special_attendance_specific: Boolean(process.use_special_attendance_specific),
      special_attendance_start: convertDateToHTMLFormat(process.special_attendance_start),
      special_attendance_end: convertDateToHTMLFormat(process.special_attendance_end),
      special_attendance_appeal_start: convertDateToHTMLFormat(process.special_attendance_appeal_start),
      special_attendance_appeal_end: convertDateToHTMLFormat(process.special_attendance_appeal_end),
      special_attendance_type_ids: Array.isArray(process.special_attendance_type_ids)
        ? process.special_attendance_type_ids.map((x) => Number(x))
        : [],
      allow_change_quota_during_registration: process.allow_change_quota_during_registration !== false,
      allow_change_course_during_registration: process.allow_change_course_during_registration !== false,
      release_grades_automatic_scanned: Boolean(process.release_grades_automatic_scanned),
      release_grades_manual: Boolean(process.release_grades_manual),
      exam_uses_answer_sheet: Boolean(process.exam_uses_answer_sheet),
      exam_uses_essay: Boolean(process.exam_uses_essay),
      essay_sheet_num_lines: Math.max(
        10,
        Math.min(30, parseInt(String(process.essay_sheet_num_lines), 10) || 28)
      ),
      exam_essay_correction_multiplier: Math.max(
        1,
        Math.min(20, parseInt(String(process.exam_essay_correction_multiplier), 10) || 4)
      ),
      exam_disqualify_on_zero_objective_area: process.exam_disqualify_on_zero_objective_area !== false,
      exam_essay_max_score: Math.max(
        1,
        Math.min(500, parseInt(String(process.exam_essay_max_score ?? 40), 10) || 40)
      ),
      exam_objective_max_points: Math.max(
        1,
        Math.min(500, parseInt(String(process.exam_objective_max_points ?? 60), 10) || 60)
      ),
      exam_objective_score_mode:
        process.exam_objective_score_mode === 'weighted_sum' ? 'weighted_sum' : 'weighted_average',
      exam_allocation_policy: process.exam_allocation_policy || 'course_location',
      exam_shortage_policy: process.exam_shortage_policy || 'mark_pending',
      exam_card_release_start: process.exam_card_release_start ? String(process.exam_card_release_start).slice(0, 10) : '',
      exam_card_release_end: process.exam_card_release_end ? String(process.exam_card_release_end).slice(0, 10) : '',
      exam_day_date: process.exam_day_date ? String(process.exam_day_date).slice(0, 10) : '',
      exam_day_gate_open: process.exam_day_gate_open || '',
      exam_day_gate_close: process.exam_day_gate_close || '',
      exam_day_exam_start: process.exam_day_exam_start || '',
      exam_day_min_stay_until: process.exam_day_min_stay_until || '',
      exam_day_exam_end: process.exam_day_exam_end || '',
      exam_day_extra_instructions: process.exam_day_extra_instructions || '',
      allow_resend_exemption_in_exemption_period: process.allow_resend_exemption_in_exemption_period !== false,
      allow_resend_special_attendance_in_request_period: process.allow_resend_special_attendance_in_request_period !== false,
      allow_resend_curricular_in_registration_period: process.allow_resend_curricular_in_registration_period !== false,
    });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (processId) => {
    if (!window.confirm('Tem certeza que deseja excluir este processo?')) {
      return;
    }
    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteProcess(processId);
      if (deleteError) {
        setError(deleteError);
        addNotification('Erro', deleteError, 'error');
      } else {
        addNotification('Sucesso', 'Processo excluído com sucesso', 'success');
        loadProcesses();
      }
    } catch (err) {
      setError('Erro ao excluir processo');
    } finally {
      setLoading(false);
    }
  }, [loadProcesses, addNotification]);

  const handleNewProcess = useCallback(() => {
    setEditingProcess(null);
    setFormData({
      name: '',
      description: '',
      code: '',
      questionnaire_id: '',
      start_date: '',
      end_date: '',
      selection_mode: usableSelectionModes[0] || 'analise_curricular',
      has_fee: false,
      enable_exemption_request: false,
      exemption_cadunico_consulta: false,
      fee_amount: 0,
      fee_payment_start: '',
      fee_payment_end: '',
      payment_method_id: '',
      use_quotas: false,
      allow_multiple_applications: false,
      is_active: true,
      exemption_start: '',
      exemption_end: '',
      exemption_review_start: '',
      exemption_review_end: '',
      registration_appeal_start: '',
      registration_appeal_end: '',
      answer_key_appeal_start: '',
      answer_key_appeal_end: '',
      result_appeal_start: '',
      result_appeal_end: '',
      enable_edital_challenge: false,
      edital_challenge_start: '',
      edital_challenge_end: '',
      edital_challenge_response_until: '',
      show_candidate_average: true,
      show_appeal_response_to_candidates: false,
      use_special_attendance_specialized: false,
      use_special_attendance_specific: false,
      special_attendance_start: '',
      special_attendance_end: '',
      special_attendance_appeal_start: '',
      special_attendance_appeal_end: '',
      special_attendance_type_ids: [],
      allow_change_quota_during_registration: true,
      allow_change_course_during_registration: true,
      release_grades_automatic_scanned: false,
      release_grades_manual: false,
      allow_resend_exemption_in_exemption_period: true,
      allow_resend_special_attendance_in_request_period: true,
      allow_resend_curricular_in_registration_period: true,
      exam_uses_answer_sheet: false,
      exam_uses_essay: false,
      essay_sheet_num_lines: 28,
      exam_essay_correction_multiplier: 4,
      exam_disqualify_on_zero_objective_area: true,
      exam_essay_max_score: 40,
      exam_objective_max_points: 60,
      exam_allocation_policy: 'course_location',
      exam_shortage_policy: 'mark_pending',
      exam_card_release_start: '',
      exam_card_release_end: '',
      exam_day_date: '',
      exam_day_gate_open: '',
      exam_day_gate_close: '',
      exam_day_exam_start: '',
      exam_day_min_stay_until: '',
      exam_day_exam_end: '',
      exam_day_extra_instructions: '',
    });
    setShowModal(true);
  }, [usableSelectionModes]);

  if (loading && processes.length === 0) {
    return <LoadingSpinner text="Carregando processos..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>📋 Gerenciamento de Processos</h2>
          <button
            onClick={handleNewProcess}
            style={{
              ...styles.button,
              ...styles.buttonPrimary
            }}
          >
            ➕ Novo Processo
          </button>
        </div>

        {error && !showModal && (
          <Alert 
            message={error} 
            type="error" 
            onClose={() => setError('')} 
          />
        )}

        {processes.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            color: '#6B7280',
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            border: '2px dashed #E5E7EB'
          }}>
            <div style={{ fontSize: '56px', marginBottom: '20px' }}>📋</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#374151' }}>Nenhum processo cadastrado</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px' }}>Clique em "Novo Processo" para criar o primeiro processo seletivo.</p>
            <button
              onClick={handleNewProcess}
              style={{ ...styles.button, ...styles.buttonPrimary }}
            >
              ➕ Novo Processo
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'name' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableCompactHeaderCell, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((prev) => nextProcessSort(prev, 'name'))}
                    title="Ordenar por nome do processo"
                  >
                    Nome{sortColumnIndicator(tableSort, 'name')}
                  </th>
                  <th style={styles.tableCompactHeaderCell}>Código</th>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'modality' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableCompactHeaderCell, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((prev) => nextProcessSort(prev, 'modality'))}
                    title="Ordenar por modalidade"
                  >
                    Modalidade{sortColumnIndicator(tableSort, 'modality')}
                  </th>
                  <th style={styles.tableCompactHeaderCell}>Período de inscrição</th>
                  <th style={styles.tableCompactHeaderCell}>Taxa</th>
                  <th style={styles.tableCompactHeaderCell}>Inscrições</th>
                  <th style={styles.tableCompactHeaderCell}>Status</th>
                  <th style={styles.tableCompactHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedProcesses.map(process => (
                  <tr key={process.id} style={styles.tableRow}>
                    <td style={styles.tableCompactCell}>
                      <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '12px' }}>
                        {process.name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>
                        {process.description}
                      </div>
                    </td>
                    <td style={styles.tableCompactCell}>
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#3B82F6', fontWeight: '600' }}>
                        {process.code || 'N/A'}
                      </div>
                    </td>
                    <td style={styles.tableCompactCell}>
                      <span style={{
                        ...styles.statusBadge,
                        fontSize: '11px',
                        padding: '2px 6px',
                        ...(process.selection_mode === 'prova'
                          ? { backgroundColor: '#FEF3C7', color: '#92400E' }
                          : process.selection_mode === 'ordem_inscricao'
                            ? { backgroundColor: '#ECFDF5', color: '#047857' }
                            : styles.statusApproved),
                      }}
                      >
                        {getSelectionModeLabel(process.selection_mode)}
                      </span>
                    </td>
                    <td style={styles.tableCompactCell}>
                      <div style={{ fontSize: '12px' }}>{formatDate(process.start_date)}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>até {formatDate(process.end_date)}</div>
                    </td>
                    <td style={styles.tableCompactCell}>
                      {process.has_fee ? (
                        <span style={{ color: '#059669', fontSize: '12px' }}>{formatCurrency(process.fee_amount)}</span>
                      ) : (
                        <span style={{ color: '#6B7280', fontSize: '12px' }}>Gratuito</span>
                      )}
                    </td>
                    <td style={styles.tableCompactCell}>
                      <span style={{
                        ...styles.statusBadge,
                        fontSize: '11px',
                        padding: '2px 6px',
                        ...(process.allow_multiple_applications ? styles.statusApproved : styles.statusPending)
                      }}>
                        {process.allow_multiple_applications ? 'Múltiplas' : 'Única'}
                      </span>
                    </td>
                    <td style={styles.tableCompactCell}>
                      <span style={{
                        ...styles.statusBadge,
                        fontSize: '11px',
                        padding: '2px 6px',
                        ...(process.is_active ? styles.statusApproved : styles.statusPending)
                      }}>
                        {process.is_active ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>
                        {process.is_active ? 'Disponível' : 'Oculto'}
                      </div>
                    </td>
                    <td style={styles.tableCompactCell}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setQuotaConfigProcess(process)}
                          style={{
                            ...styles.button,
                            ...styles.buttonSecondary,
                            ...styles.actionBtnIcon
                          }}
                          title={process.use_quotas ? 'Tabela de cotas' : 'Desempate'}
                        >
                          <span>{process.use_quotas ? '📋' : '🔀'}</span>
                          <span style={{ fontSize: '9px', lineHeight: 1 }}>{process.use_quotas ? 'Tabela de cotas' : 'Desempate'}</span>
                        </button>
                        <button
                          onClick={() => { setDocsModalProcess(process); setDocFormData({ name: '', published_at: '' }); setDocFormError(''); }}
                          style={{
                            ...styles.button,
                            ...styles.buttonSecondary,
                            ...styles.actionBtnIcon
                          }}
                          title="Editais"
                        >
                          <span>📄</span>
                          <span style={{ fontSize: '9px', lineHeight: 1 }}>Editais</span>
                        </button>
                        <button
                          onClick={() => { setScheduleModalProcess(process); setEditingScheduleItem(null); setScheduleFormData({ label: '', date_type: 'period', single_date: '', start_date: '', end_date: '' }); setScheduleFormError(''); }}
                          style={{
                            ...styles.button,
                            ...styles.buttonSecondary,
                            ...styles.actionBtnIcon
                          }}
                          title="Cronograma"
                        >
                          <span>📅</span>
                          <span style={{ fontSize: '9px', lineHeight: 1 }}>Cronograma</span>
                        </button>
                        {process.selection_mode === 'prova' && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setExamVenuesModalProcess({ id: process.id, name: process.name });
                                setVenueForm({ name: '', street: '', neighborhood: '', city_state: '' });
                                setRoomForm({ venueId: '', name: '', building: '', notes: '', capacity: '' });
                              }}
                              style={{
                                ...styles.button,
                                ...styles.buttonSecondary,
                                ...styles.actionBtnIcon
                              }}
                              title="Locais de prova e salas"
                            >
                              <span>📍</span>
                              <span style={{ fontSize: '9px', lineHeight: 1 }}>Locais</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setGabaritoModeloModalProcess(process)}
                              style={{
                                ...styles.button,
                                ...styles.buttonSecondary,
                                ...styles.actionBtnIcon
                              }}
                              title="Gabarito — disciplinas/áreas, questões e pesos (processo)"
                            >
                              <span>📐</span>
                              <span style={{ fontSize: '9px', lineHeight: 1 }}>Gabarito</span>
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEdit(process)}
                          style={{
                            ...styles.button,
                            ...styles.buttonSecondary,
                            ...styles.actionBtnIcon
                          }}
                          title="Editar"
                        >
                          <span>✏️</span>
                          <span style={{ fontSize: '9px', lineHeight: 1 }}>Editar</span>
                        </button>
                        <button
                          onClick={() => handleDelete(process.id)}
                          style={{
                            ...styles.button,
                            ...styles.buttonDanger,
                            ...styles.actionBtnIcon
                          }}
                          title="Excluir"
                        >
                          <span>🗑️</span>
                          <span style={{ fontSize: '9px', lineHeight: 1 }}>Excluir</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingProcess ? 'Editar Processo' : 'Novo Processo'}
        large
      >
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Alert
              message={error}
              type="error"
              onClose={() => setError('')}
            />
          </div>
        )}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Nome do Processo *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={styles.input}
                placeholder="Ex: Processo Seletivo 2024.1"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Código do Processo</label>
              <input
                type="text"
                value={formData.code}
                style={{
                  ...styles.input,
                  backgroundColor: '#F3F4F6',
                  cursor: 'not-allowed'
                }}
                placeholder="Gerado automaticamente"
                readOnly
              />
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Código único gerado automaticamente
              </div>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Descrição</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={styles.input}
                placeholder="Descrição do processo"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Período de Inscrição - Início *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                style={styles.input}
                required
              />
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Use o calendário ou digite no formato DD/MM/AAAA
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Período de Inscrição - Fim *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                style={styles.input}
                required
              />
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Use o calendário ou digite no formato DD/MM/AAAA
              </div>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
              <label style={styles.label}>Modalidade do processo seletivo *</label>
              <select
                value={formData.selection_mode}
                onChange={(e) => handleInputChange('selection_mode', e.target.value)}
                style={styles.select}
              >
                {selectionModeOptions.map((modeId) => (
                  <option key={modeId} value={modeId}>
                    {getSelectionModeLabel(modeId)}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                {usableSelectionModes.length === 0
                  ? 'Nenhuma modalidade contratada para esta instituição. Contate o proprietário do sistema.'
                  : 'Somente modalidades contratadas para sua instituição. Barema e sorteio ficam disponíveis quando forem implementadas e contratadas.'}
              </div>
            </div>
          </div>

          <div style={processFormPanel}>
            <h4 style={processFormPanelTitle}>Sistema de cotas</h4>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
              Defina primeiro se o processo utiliza cotas (AC, PCD, etc.). Opções de alteração de cota na inscrição só aparecem quando este item estiver marcado.
            </p>
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.use_quotas}
                    onChange={(e) => handleInputChange('use_quotas', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Usar sistema de cotas
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se marcado, ao cadastrar cursos será possível definir vagas por cota (AC, PCD, etc.)
                </div>
              </div>
            </div>
          </div>

          <div style={processFormPanel}>
            <h4 style={processFormPanelTitle}>Durante o período de inscrição</h4>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
              Enquanto as inscrições estiverem abertas (datas acima), o candidato poderá ou não alterar cota e curso na área do candidato, conforme as opções abaixo.
            </p>
            {formData.use_quotas && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.allow_change_quota_during_registration}
                    onChange={(e) => handleInputChange('allow_change_quota_during_registration', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Permitir alterar cota
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se desmarcado, o candidato não poderá usar &quot;Alterar cota&quot; durante o período de inscrições.
                </div>
              </div>
            </div>
            )}
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.allow_change_course_during_registration}
                    onChange={(e) => handleInputChange('allow_change_course_during_registration', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Permitir alterar curso
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se desmarcado, o candidato não poderá usar &quot;Trocar curso&quot; nem fluxos equivalentes durante o período de inscrições.
                </div>
              </div>
            </div>
          </div>

          <div style={processFormPanel}>
            <h4 style={processFormPanelTitle}>Taxa de inscrição e isenção</h4>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 14px 0' }}>
              As opções abaixo liberam períodos de pagamento ou de pedido de isenção, como no edital.
            </p>

            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.has_fee}
                    onChange={(e) => handleInputChange('has_fee', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Possui taxa de inscrição
                </label>
              </div>
            </div>
            {formData.has_fee && (
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>Valor da Taxa</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fee_amount === 0 || formData.fee_amount == null ? '' : formData.fee_amount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        handleInputChange('fee_amount', 0);
                        return;
                      }
                      const parsed = parseFloat(String(raw).replace(',', '.')) || 0;
                      const rounded = Math.round(parsed * 100) / 100;
                      handleInputChange('fee_amount', rounded);
                    }}
                    style={styles.input}
                    placeholder="0,00"
                  />
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    {formData.fee_amount > 0 ? `Exibição: ${formatCurrency(formData.fee_amount)}` : 'Informe o valor em reais (máx. 2 casas decimais)'}
                  </div>
                </div>
              </div>
            )}

          {formData.has_fee && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Período de Pagamento da Taxa - Início</label>
                <input
                  type="date"
                  value={formData.fee_payment_start}
                  onChange={(e) => handleInputChange('fee_payment_start', e.target.value)}
                  style={styles.input}
                />
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Data a partir da qual o pagamento da taxa pode ser realizado
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Período de Pagamento da Taxa - Fim</label>
                <input
                  type="date"
                  value={formData.fee_payment_end}
                  onChange={(e) => handleInputChange('fee_payment_end', e.target.value)}
                  style={styles.input}
                />
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Data limite para pagamento da taxa
                </div>
              </div>
            </div>
          )}

          {formData.has_fee && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Método de pagamento</label>
                <select
                  value={formData.payment_method_id}
                  onChange={(e) => handleInputChange('payment_method_id', e.target.value)}
                  style={styles.select}
                >
                  <option value="">Selecione o método de pagamento...</option>
                  {paymentMethods.map(pm => (
                    <option key={pm.id} value={pm.id}>{pm.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Cadastre métodos em Configurações → Métodos de pagamento
                </div>
              </div>
            </div>
          )}

          {formData.has_fee && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.enable_exemption_request}
                    onChange={(e) => handleInputChange('enable_exemption_request', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Permitir pedido de isenção
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Desative quando o edital não tiver período de solicitação de isenção.
                </div>
              </div>
            </div>
          )}


          {formData.has_fee && formData.enable_exemption_request && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={Boolean(formData.exemption_cadunico_consulta)}
                    disabled={!cadunicoReady}
                    onChange={(e) => handleInputChange('exemption_cadunico_consulta', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Consulta direta CadÚnico (NIS)
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  {cadunicoReady
                    ? 'Com esta opção, o candidato informa o NIS; documentos de isenção ficam opcionais. O analista consulta o CadÚnico e ainda defer/indefer manualmente.'
                    : 'Indisponível: habilite a integração CadÚnico em Configurações (stub ou credenciais MDS).'}
                </div>
              </div>
            </div>
          )}

          {formData.has_fee && formData.enable_exemption_request && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Período de Pedido de Isenção - Início</label>
                <input
                  type="date"
                  value={formData.exemption_start}
                  onChange={(e) => handleInputChange('exemption_start', e.target.value)}
                  style={styles.input}
                  required
                />
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Data a partir da qual candidatos podem solicitar isenção
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Período de Pedido de Isenção - Fim</label>
                <input
                  type="date"
                  value={formData.exemption_end}
                  onChange={(e) => handleInputChange('exemption_end', e.target.value)}
                  style={styles.input}
                  required
                />
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Após esta data, inscrição apenas com pagamento
                </div>
              </div>
            </div>
          )}
          </div>

          {formData.selection_mode === 'prova' && (
            <div style={processFormPanel}>
              <h4 style={processFormPanelTitle}>Reenvio de documentos pelo candidato</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
                Marque em quais períodos o candidato pode enviar novamente documentos após indeferimento (atendimento especial{formData.has_fee ? ' ou isenção' : ''}), quando as demais regras do sistema forem atendidas.
              </p>
              {formData.has_fee && (
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.allow_resend_exemption_in_exemption_period}
                      onChange={(e) => handleInputChange('allow_resend_exemption_in_exemption_period', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Permitir reenvio durante o período de pedido de isenção
                  </label>
                </div>
              </div>
              )}
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.allow_resend_special_attendance_in_request_period}
                      onChange={(e) => handleInputChange('allow_resend_special_attendance_in_request_period', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Permitir reenvio durante o período de pedido de atendimento especial
                  </label>
                </div>
              </div>
            </div>
          )}

          {(formData.selection_mode === 'analise_curricular' || formData.selection_mode === 'ordem_inscricao') && (
            <div style={processFormPanel}>
              <h4 style={processFormPanelTitle}>Reenvio de documentos pelo candidato</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
                Marque em quais períodos o candidato pode reenviar documentos da inscrição após indeferimento
                {formData.selection_mode === 'ordem_inscricao'
                  ? ' na análise de inscrição'
                  : ' na análise curricular'}
                {formData.has_fee ? ' ou na isenção' : ''}.
              </p>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.allow_resend_curricular_in_registration_period}
                      onChange={(e) => handleInputChange('allow_resend_curricular_in_registration_period', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Permitir reenvio durante o período de inscrição
                  </label>
                </div>
              </div>
              {formData.has_fee && (
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.allow_resend_exemption_in_exemption_period}
                      onChange={(e) => handleInputChange('allow_resend_exemption_in_exemption_period', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Permitir reenvio durante o período de pedido de isenção
                  </label>
                </div>
              </div>
              )}
            </div>
          )}

          {formData.selection_mode === 'prova' && (
            <div style={processFormPanel}>
              <h4 style={processFormPanelTitle}>Lançamento de notas (prova)</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
                Indica como as notas devem entrar no sistema neste processo. Por enquanto só configuração; a fila em <strong>Inscritos</strong> e integrações (gabarito / digitação) virão depois.
              </p>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.release_grades_automatic_scanned}
                      onChange={(e) => handleInputChange('release_grades_automatic_scanned', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Automático (leitura de gabarito escaneado)
                  </label>
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.release_grades_manual}
                      onChange={(e) => handleInputChange('release_grades_manual', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Manual
                  </label>
                </div>
              </div>
            </div>
          )}

          {formData.selection_mode === 'prova' && (
            <div style={processFormPanel}>
              <h4 style={processFormPanelTitle}>Prova — folhas (PDF)</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
                Marque o que a prova terá (impressão do ZIP). A quantidade de questões na folha objetiva vem do <strong>Gabarito</strong> deste processo (áreas, quantidades e pesos). Use o botão <strong>Gabarito</strong> na lista para editar; <strong>Locais</strong> cadastra onde a prova será aplicada. O ZIP é gerado em <strong>Inscritos</strong> (modo prova).
              </p>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.exam_uses_answer_sheet}
                      onChange={(e) => handleInputChange('exam_uses_answer_sheet', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Gabarito (folha de respostas objetivas)
                  </label>
                </div>
              </div>
              {formData.exam_uses_answer_sheet && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    background: '#F0F9FF',
                    borderRadius: 8,
                    border: '1px solid #BAE6FD',
                  }}
                >
                  <strong style={{ fontSize: 13, color: '#0C4A6E', display: 'block', marginBottom: 10 }}>
                    Cálculo da nota objetiva
                  </strong>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="exam_objective_score_mode"
                      checked={formData.exam_objective_score_mode !== 'weighted_sum'}
                      onChange={() => handleInputChange('exam_objective_score_mode', 'weighted_average')}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                      <strong>Média ponderada:</strong> Σ(acertos × peso) ÷ Σ(peso). Ex.: 16 acertos em português (peso 2) e 24
                      em matemática (peso 3) → (16×2 + 24×3) ÷ (2+3) = 20,8.
                    </span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="exam_objective_score_mode"
                      checked={formData.exam_objective_score_mode === 'weighted_sum'}
                      onChange={() => handleInputChange('exam_objective_score_mode', 'weighted_sum')}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                      <strong>Soma:</strong> Σ(acertos × peso). Ex.: 16 acertos em português (peso 2) e 24 em matemática (peso 3)
                      → 16×2 + 24×3 = 104 pontos na objetiva.
                    </span>
                  </label>
                  <p style={{ fontSize: 11, color: '#64748B', margin: '10px 0 0 0', lineHeight: 1.45 }}>
                    Em ambos os modos, o sistema grava <strong>acertos por área</strong> (número absoluto; OMR conta automaticamente).
                    Alterar o modo não recalcula notas já lançadas.
                  </p>
                </div>
              )}
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.exam_uses_essay}
                      onChange={(e) => {
                        const next = e.target.checked;
                        if (next) {
                          const cur = Math.max(
                            10,
                            Math.min(30, parseInt(String(formData.essay_sheet_num_lines), 10) || 28)
                          );
                          setEssayLinesDraft(String(cur));
                          setEssayLinesModalOpen(true);
                        } else {
                          handleInputChange('exam_uses_essay', false);
                        }
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    Redação
                  </label>
                  {formData.exam_uses_essay && (
                    <button
                      type="button"
                      onClick={() => {
                        setEssayLinesDraft(
                          String(
                            Math.max(
                              10,
                              Math.min(30, parseInt(String(formData.essay_sheet_num_lines), 10) || 28)
                            )
                          )
                        );
                        setEssayLinesModalOpen(true);
                      }}
                      style={{
                        marginLeft: 12,
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: '1px solid #D1D5DB',
                        background: '#fff',
                        cursor: 'pointer',
                        color: '#374151',
                      }}
                    >
                      Número de linhas ({formData.essay_sheet_num_lines})
                    </button>
                  )}
                </div>
              </div>
              {formData.exam_uses_essay && (
                <div
                  style={{
                    marginTop: 16,
                    padding: '12px 14px',
                    background: '#F8FAFC',
                    borderRadius: 8,
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <strong style={{ fontSize: 13, color: '#1E3A5F', display: 'block', marginBottom: 8 }}>
                    Total da prova (soma em pontos)
                  </strong>
                  <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                    Parte objetiva no total: <strong>{objectivePartMaxPointsHint.n} pontos</strong>
                    {objectivePartMaxPointsHint.fromGabarito ? (
                      <> (soma das questões em <strong>Gabarito</strong>).</>
                    ) : (
                      <>
                        {' '}
                        (cadastre quantidades em <strong>Gabarito</strong> na lista; até lá o sistema usa o valor gravado no
                        processo.)
                      </>
                    )}
                  </p>
                  <div style={styles.formRow}>
                    <label style={{ ...styles.label, minWidth: '200px' }}>Máx. da redação (pontos)</label>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={formData.exam_essay_max_score}
                      onChange={(e) =>
                        handleInputChange(
                          'exam_essay_max_score',
                          Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 40))
                        )
                      }
                      style={{ ...styles.input, maxWidth: '100px' }}
                    />
                  </div>
                  <p style={{ fontSize: '11px', color: '#64748B', margin: '4px 0 0 0', lineHeight: 1.45 }}>
                    A coluna <strong>Total</strong> em Inscritos = <strong>nota objetiva</strong> (Σ acertos×peso ou média disso) +{' '}
                    <strong>redação</strong>. O Gabarito define questões e pesos por área; não há escala 0–10.
                  </p>
                </div>
              )}
              {formData.exam_uses_essay && (
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop: '1px solid #E5E7EB',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <strong style={{ fontSize: 13, color: '#374151' }}>Corte para correção da redação</strong>
                  <div style={styles.formRow}>
                    <label style={{ ...styles.label, minWidth: '220px' }}>
                      Multiplicador (× vagas) p/ correção da redação
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={formData.exam_essay_correction_multiplier}
                      onChange={(e) =>
                        handleInputChange(
                          'exam_essay_correction_multiplier',
                          Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 4))
                        )
                      }
                      style={{ ...styles.input, maxWidth: '100px' }}
                    />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>
                      <input
                        type="checkbox"
                        checked={formData.exam_disqualify_on_zero_objective_area}
                        onChange={(e) => handleInputChange('exam_disqualify_on_zero_objective_area', e.target.checked)}
                        style={{ marginRight: 8 }}
                      />
                      Desclassificar na objetiva quem zerar em qualquer área com peso maior que zero
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {formData.selection_mode === 'prova' && (
            <div style={processFormPanel}>
              <h4 style={processFormPanelTitle}>Alocação da prova</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' }}>
                Define como os candidatos serão distribuídos entre locais/salas e o comportamento quando a capacidade não for suficiente.
              </p>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={{ ...styles.label, marginBottom: 6 }}>Política de alocação</label>
                  <label style={{ ...styles.label, display: 'block', fontWeight: 400 }}>
                    <input
                      type="radio"
                      name="exam_allocation_policy"
                      checked={formData.exam_allocation_policy === 'course_location'}
                      onChange={() => handleInputChange('exam_allocation_policy', 'course_location')}
                      style={{ marginRight: '8px' }}
                    />
                    Por local de oferta do curso
                  </label>
                  <label style={{ ...styles.label, display: 'block', fontWeight: 400 }}>
                    <input
                      type="radio"
                      name="exam_allocation_policy"
                      checked={formData.exam_allocation_policy === 'candidate_choice'}
                      onChange={() => handleInputChange('exam_allocation_policy', 'candidate_choice')}
                      style={{ marginRight: '8px' }}
                    />
                    Por local escolhido pelo candidato
                  </label>
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={{ ...styles.label, marginBottom: 6 }}>Se faltar vaga</label>
                  <label style={{ ...styles.label, display: 'block', fontWeight: 400 }}>
                    <input
                      type="radio"
                      name="exam_shortage_policy"
                      checked={formData.exam_shortage_policy === 'mark_pending'}
                      onChange={() => handleInputChange('exam_shortage_policy', 'mark_pending')}
                      style={{ marginRight: '8px' }}
                    />
                    Marcar pendente para ajuste manual
                  </label>
                  <label style={{ ...styles.label, display: 'block', fontWeight: 400 }}>
                    <input
                      type="radio"
                      name="exam_shortage_policy"
                      checked={formData.exam_shortage_policy === 'block_export'}
                      onChange={() => handleInputChange('exam_shortage_policy', 'block_export')}
                      style={{ marginRight: '8px' }}
                    />
                    Bloquear exportação até resolver
                  </label>
                </div>
              </div>

              <div style={{ ...processFormPanel, marginTop: '16px' }}>
                <h4 style={processFormPanelTitle}>Dia da prova e cartão de inscrição</h4>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 14px 0' }}>
                  Liberação do cartão definitivo no portal (homologado, com local definido) e horários exibidos no PDF.
                  As datas de liberação e da prova também aparecem no cronograma do processo.
                </p>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: '1 1 220px' }}>
                    <label style={styles.label}>Liberação do cartão — início</label>
                    <input
                      type="date"
                      value={formData.exam_card_release_start || ''}
                      onChange={(e) => handleInputChange('exam_card_release_start', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: '1 1 220px' }}>
                    <label style={styles.label}>Liberação do cartão — fim (opcional)</label>
                    <input
                      type="date"
                      value={formData.exam_card_release_end || ''}
                      onChange={(e) => handleInputChange('exam_card_release_end', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: '1 1 220px' }}>
                    <label style={styles.label}>Data da realização das provas</label>
                    <input
                      type="date"
                      value={formData.exam_day_date || ''}
                      onChange={(e) => handleInputChange('exam_day_date', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: '1 1 200px' }}>
                    <label style={styles.label}>Abertura dos portões</label>
                    <input
                      type="text"
                      value={formData.exam_day_gate_open || ''}
                      onChange={(e) => handleInputChange('exam_day_gate_open', e.target.value)}
                      style={styles.input}
                      placeholder="Ex.: 07h00"
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: '1 1 200px' }}>
                    <label style={styles.label}>Fechamento dos portões</label>
                    <input
                      type="text"
                      value={formData.exam_day_gate_close || ''}
                      onChange={(e) => handleInputChange('exam_day_gate_close', e.target.value)}
                      style={styles.input}
                      placeholder="Ex.: 08h30"
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: '1 1 200px' }}>
                    <label style={styles.label}>Início da prova</label>
                    <input
                      type="text"
                      value={formData.exam_day_exam_start || ''}
                      onChange={(e) => handleInputChange('exam_day_exam_start', e.target.value)}
                      style={styles.input}
                      placeholder="Ex.: 09h00"
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: '1 1 240px' }}>
                    <label style={styles.label}>Permanência mínima na sala</label>
                    <input
                      type="text"
                      value={formData.exam_day_min_stay_until || ''}
                      onChange={(e) => handleInputChange('exam_day_min_stay_until', e.target.value)}
                      style={styles.input}
                      placeholder="Ex.: até 10h30 para sair"
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: '1 1 200px' }}>
                    <label style={styles.label}>Término da prova</label>
                    <input
                      type="text"
                      value={formData.exam_day_exam_end || ''}
                      onChange={(e) => handleInputChange('exam_day_exam_end', e.target.value)}
                      style={styles.input}
                      placeholder="Ex.: 13h00"
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%' }}>
                    <label style={styles.label}>Instruções adicionais (opcional)</label>
                    <textarea
                      value={formData.exam_day_extra_instructions || ''}
                      onChange={(e) => handleInputChange('exam_day_extra_instructions', e.target.value)}
                      style={{ ...styles.input, minHeight: '72px', resize: 'vertical' }}
                      placeholder="Ex.: documentos obrigatórios, proibido uso de celular..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={styles.formRow}>
            <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
              <label style={styles.label}>Questionário Socioeconômico</label>
              <select
                value={formData.questionnaire_id}
                onChange={(e) => handleInputChange('questionnaire_id', e.target.value)}
                style={styles.select}
              >
                <option value="">Nenhum</option>
                {questionnaires.map(q => (
                  <option key={q.id} value={q.id}>{q.name}</option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Candidatos responderão este questionário durante a inscrição
              </div>
            </div>
          </div>

          {formData.selection_mode === 'prova' && (
            <div style={processFormPanel}>
              <h4 style={processFormPanelTitle}>Atendimento especial (prova)</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 14px 0' }}>
                Marque as modalidades de atendimento para liberar o período de pedido e os tipos vinculados ao processo.
              </p>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.use_special_attendance_specialized}
                      onChange={(e) => handleInputChange('use_special_attendance_specialized', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Habilitar atendimento especializado
                  </label>
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.use_special_attendance_specific}
                      onChange={(e) => handleInputChange('use_special_attendance_specific', e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Habilitar atendimento específico
                  </label>
                </div>
              </div>
              {(formData.use_special_attendance_specialized || formData.use_special_attendance_specific) && (
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Período de pedido de atendimento especial - Início</label>
                    <input
                      type="date"
                      value={formData.special_attendance_start}
                      onChange={(e) => handleInputChange('special_attendance_start', e.target.value)}
                      style={styles.input}
                    />
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      Data a partir da qual o candidato pode solicitar atendimento especial
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Período de pedido de atendimento especial - Fim</label>
                    <input
                      type="date"
                      value={formData.special_attendance_end}
                      onChange={(e) => handleInputChange('special_attendance_end', e.target.value)}
                      style={styles.input}
                    />
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      Data limite para envio do pedido de atendimento especial
                    </div>
                  </div>
                </div>
              )}
              <div style={{ marginTop: '12px' }}>
                <span style={{ ...styles.label, display: 'block', marginBottom: '6px' }}>Tipos permitidos neste processo</span>
                {specialAttendanceTypes.length === 0 ? (
                  <p style={{ fontSize: '12px', color: '#6B7280' }}>Cadastre tipos em Configuração → Tipos atendimento especial.</p>
                ) : (
                  specialAttendanceTypes.map((t) => {
                    const ids = formData.special_attendance_type_ids || [];
                    const checked = ids.includes(Number(t.id));
                    return (
                      <label key={t.id} style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const n = Number(t.id);
                            const cur = formData.special_attendance_type_ids || [];
                            if (checked) {
                              handleInputChange('special_attendance_type_ids', cur.filter((x) => Number(x) !== n));
                            } else {
                              handleInputChange('special_attendance_type_ids', [...cur.map(Number).filter((x) => x !== n), n]);
                            }
                          }}
                          style={{ marginRight: '6px' }}
                        />
                        {t.name} ({t.category === 'specific' ? 'específico' : 'especializado'})
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#F3F4F6', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>Períodos de Recursos</h4>
            
            {formData.has_fee && formData.enable_exemption_request && (
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Recurso contra o resultado da isenção - Início</label>
                  <input
                    type="date"
                    value={formData.exemption_review_start}
                    onChange={(e) => handleInputChange('exemption_review_start', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Recurso contra o resultado da isenção - Fim</label>
                  <input
                    type="date"
                    value={formData.exemption_review_end}
                    onChange={(e) => handleInputChange('exemption_review_end', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>
            )}

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Recurso contra a Lista de inscritos - Início</label>
                <input
                  type="date"
                  value={formData.registration_appeal_start}
                  onChange={(e) => handleInputChange('registration_appeal_start', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Recurso contra a Lista de inscritos - Fim</label>
                <input
                  type="date"
                  value={formData.registration_appeal_end}
                  onChange={(e) => handleInputChange('registration_appeal_end', e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            {formData.selection_mode === 'prova'
              && (formData.use_special_attendance_specialized || formData.use_special_attendance_specific) && (
              <>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Recurso contra o Resultado preliminar dos pedidos de atendimento especializado/específico - Início</label>
                  <input
                    type="date"
                    value={formData.special_attendance_appeal_start}
                    onChange={(e) => handleInputChange('special_attendance_appeal_start', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Recurso contra o Resultado preliminar dos pedidos de atendimento especializado/específico - Fim</label>
                  <input
                    type="date"
                    value={formData.special_attendance_appeal_end}
                    onChange={(e) => handleInputChange('special_attendance_appeal_end', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px 0', flex: '1 1 100%' }}>
                Se estas datas estiverem vazias, o candidato poderá interpor recurso de atendimento especial no mesmo período de
                {' '}<strong>Recurso contra o Resultado Preliminar</strong> da inscrição (abaixo), desde que o pedido de AT especial esteja indeferido.
              </p>
              </>
            )}

            <div style={styles.formRow}>
              {formData.selection_mode === 'prova' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Recurso contra o Gabarito - Início</label>
                    <input
                      type="date"
                      value={formData.answer_key_appeal_start}
                      onChange={(e) => handleInputChange('answer_key_appeal_start', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Recurso contra o Gabarito - Fim</label>
                    <input
                      type="date"
                      value={formData.answer_key_appeal_end}
                      onChange={(e) => handleInputChange('answer_key_appeal_end', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </>
              )}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Recurso contra o Resultado Preliminar - Início</label>
                <input
                  type="date"
                  value={formData.result_appeal_start}
                  onChange={(e) => handleInputChange('result_appeal_start', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Recurso contra o Resultado Preliminar - Fim</label>
                <input
                  type="date"
                  value={formData.result_appeal_end}
                  onChange={(e) => handleInputChange('result_appeal_end', e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={Boolean(formData.enable_edital_challenge)}
                  onChange={(e) => handleInputChange('enable_edital_challenge', e.target.checked)}
                />
                Permitir impugnação ao edital no sistema (formulário público, sem login)
              </label>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '6px 0 12px 0' }}>
                Quando marcado e dentro do prazo, a tela de login exibe o botão para qualquer pessoa protocolar impugnação.
              </p>
              {formData.enable_edital_challenge && (
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Impugnação ao edital - Início</label>
                    <input
                      type="date"
                      value={formData.edital_challenge_start}
                      onChange={(e) => handleInputChange('edital_challenge_start', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Impugnação ao edital - Fim</label>
                    <input
                      type="date"
                      value={formData.edital_challenge_end}
                      onChange={(e) => handleInputChange('edital_challenge_end', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </div>
              )}
              {formData.enable_edital_challenge && (
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%' }}>
                    <label style={styles.label}>Consulta pública da resposta — válida até</label>
                    <input
                      type="date"
                      value={formData.edital_challenge_response_until}
                      onChange={(e) => handleInputChange('edital_challenge_response_until', e.target.value)}
                      style={styles.input}
                    />
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: '6px 0 0' }}>
                      Até esta data o botão «Consultar protocolo» permanece na tela de login (transparência).
                      Se vazio, usa o fim do prazo de impugnação.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ ...processFormPanel, marginTop: '20px' }}>
            <h4 style={processFormPanelTitle}>Outras opções do processo</h4>
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.allow_multiple_applications}
                    onChange={(e) => handleInputChange('allow_multiple_applications', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Permitir inscrições diversas
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se marcado, candidatos podem se inscrever em múltiplos cursos
                </div>
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Processo ativo
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Processos ativos aparecem para candidatos e permitem inscrições
                </div>
              </div>
            </div>
            {(formData.selection_mode === 'analise_curricular' || formData.selection_mode === 'carta_intencao') && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.show_candidate_average}
                    onChange={(e) => handleInputChange('show_candidate_average', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Exibir média do candidato na área do candidato
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se marcado, o candidato verá sua média no selo quando deferido na análise curricular
                </div>
              </div>
            </div>
            )}
            {formData.selection_mode === 'prova' && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.show_candidate_average}
                    onChange={(e) => handleInputChange('show_candidate_average', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Exibir nota do candidato na área do candidato
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se marcado, o candidato verá sua nota no selo quando o resultado da prova estiver disponível
                </div>
              </div>
            </div>
            )}
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%', maxWidth: '100%', minWidth: 0 }}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.show_appeal_response_to_candidates}
                    onChange={(e) => handleInputChange('show_appeal_response_to_candidates', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Exibir resposta de recursos aos candidatos (após publicação pelo admin)
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se marcado, o administrador poderá publicar decisão e fundamentação em &quot;Ver recurso&quot; no portal do candidato, quando o recurso já estiver julgado.
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Salvando...' : (editingProcess ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!examVenuesModalProcess}
        onClose={() => setExamVenuesModalProcess(null)}
        title={examVenuesModalProcess ? `Locais de prova — ${examVenuesModalProcess.name}` : ''}
      >
        <div style={{ maxWidth: 560 }}>
          {examVenuesLoading && <p style={{ color: '#6B7280', fontSize: 14 }}>Carregando…</p>}
          <div style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 8 }}>
            <strong style={{ fontSize: 13 }}>Novo local</strong>
            <div style={{ marginTop: 8 }}>
              <input
                type="text"
                placeholder="Nome do local"
                value={venueForm.name}
                onChange={(e) => setVenueForm((f) => ({ ...f, name: e.target.value }))}
                style={{ ...styles.input, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Rua"
                value={venueForm.street}
                onChange={(e) => setVenueForm((f) => ({ ...f, street: e.target.value }))}
                style={{ ...styles.input, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Bairro"
                value={venueForm.neighborhood}
                onChange={(e) => setVenueForm((f) => ({ ...f, neighborhood: e.target.value }))}
                style={{ ...styles.input, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Cidade/UF (ex.: PIRAPORA MG)"
                value={venueForm.city_state}
                onChange={(e) => setVenueForm((f) => ({ ...f, city_state: e.target.value }))}
                onBlur={(e) => setVenueForm((f) => ({ ...f, city_state: normalizeCityStateLabel(e.target.value) }))}
                style={{ ...styles.input, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
              />
              <button
                type="button"
                style={{ ...styles.button, ...styles.buttonPrimary }}
                onClick={async () => {
                  if (!examVenuesModalProcess?.id || !venueForm.name.trim()) {
                    addNotification('Atenção', 'Informe o nome do local', 'warning');
                    return;
                  }
                  const normalizedCityState = normalizeCityStateLabel(venueForm.city_state);
                  if (!isCityStateLabelValid(normalizedCityState)) {
                    addNotification('Atenção', 'Cidade/UF deve estar no formato CIDADE UF (ex.: PIRAPORA MG)', 'warning');
                    return;
                  }
                  try {
                    await apiClient.createExamVenue(examVenuesModalProcess.id, {
                      name: venueForm.name.trim(),
                      street: venueForm.street.trim() || undefined,
                      neighborhood: venueForm.neighborhood.trim() || undefined,
                      city_state: normalizedCityState,
                      rooms: [],
                    });
                    setVenueForm({ name: '', street: '', neighborhood: '', city_state: '' });
                    await loadExamVenues(examVenuesModalProcess.id);
                    addNotification('OK', 'Local adicionado', 'success');
                  } catch (e) {
                    addNotification('Erro', e.message || 'Erro', 'error');
                  }
                }}
              >
                Adicionar local
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 14, padding: 12, background: '#EFF6FF', borderRadius: 8 }}>
            <strong style={{ fontSize: 13 }}>Nova sala</strong>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <select
                value={roomForm.venueId}
                onChange={(e) => setRoomForm((f) => ({ ...f, venueId: e.target.value }))}
                style={{ ...styles.select, width: '100%', boxSizing: 'border-box' }}
              >
                <option value="">Selecione o local…</option>
                {(examVenuesList || []).map((v) => (
                  <option key={v.id} value={String(v.id)}>
                    {v.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Nome ou número da sala"
                value={roomForm.name}
                onChange={(e) => setRoomForm((f) => ({ ...f, name: e.target.value }))}
                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Prédio ou unidade (opcional)"
                value={roomForm.building}
                onChange={(e) => setRoomForm((f) => ({ ...f, building: e.target.value }))}
                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Observação (opcional)"
                value={roomForm.notes}
                onChange={(e) => setRoomForm((f) => ({ ...f, notes: e.target.value }))}
                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              />
              <input
                type="number"
                min={1}
                placeholder="Capacidade da sala (ex.: 30)"
                value={roomForm.capacity}
                onChange={(e) => setRoomForm((f) => ({ ...f, capacity: e.target.value }))}
                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              />
              <button
                type="button"
                style={{ ...styles.button, ...styles.buttonPrimary }}
                onClick={async () => {
                  const vid = parseInt(roomForm.venueId, 10);
                  const cap = parseInt(roomForm.capacity, 10);
                  if (!vid || !roomForm.name.trim() || !Number.isFinite(cap) || cap < 1) {
                    addNotification('Atenção', 'Selecione o local, informe o nome da sala e a capacidade', 'warning');
                    return;
                  }
                  try {
                    await apiClient.createExamRoom(vid, {
                      name: roomForm.name.trim(),
                      building: roomForm.building.trim() || undefined,
                      notes: roomForm.notes.trim() || undefined,
                      capacity: cap
                    });
                    setRoomForm({ venueId: String(vid), name: '', building: '', notes: '', capacity: '' });
                    await loadExamVenues(examVenuesModalProcess?.id);
                    addNotification('OK', 'Sala adicionada', 'success');
                  } catch (e) {
                    addNotification('Erro', e.message || 'Erro', 'error');
                  }
                }}
              >
                Adicionar sala
              </button>
            </div>
          </div>

          {(examVenuesList || []).map((v) => (
            <div key={v.id} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{v.name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    {[v.street, v.neighborhood, v.city_state].filter(Boolean).join(' - ') || v.address || 'Endereço não informado'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 11, whiteSpace: 'nowrap' }}
                    onClick={async () => {
                      const newName = (window.prompt('Nome do local', v.name || '') || '').trim();
                      if (!newName) return;
                      const newStreet = (window.prompt('Rua', v.street || '') || '').trim();
                      const newNeighborhood = (window.prompt('Bairro', v.neighborhood || '') || '').trim();
                      const newCityState = normalizeCityStateLabel((window.prompt('Cidade/UF (ex.: PIRAPORA MG)', v.city_state || '') || '').trim());
                      if (!isCityStateLabelValid(newCityState)) {
                        addNotification('Atenção', 'Cidade/UF deve estar no formato CIDADE UF (ex.: PIRAPORA MG)', 'warning');
                        return;
                      }
                      try {
                        await apiClient.updateExamVenue(v.id, {
                          name: newName,
                          street: newStreet || '',
                          neighborhood: newNeighborhood || '',
                          city_state: newCityState,
                        });
                        await loadExamVenues(examVenuesModalProcess?.id);
                        addNotification('OK', 'Local atualizado', 'success');
                      } catch (e) {
                        addNotification('Erro', e.message || 'Erro', 'error');
                      }
                    }}
                  >
                    Editar local
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 11, whiteSpace: 'nowrap' }}
                    onClick={async () => {
                      if (!window.confirm('Excluir este local e todas as salas?')) return;
                      try {
                        await apiClient.deleteExamVenue(v.id);
                        await loadExamVenues(examVenuesModalProcess?.id);
                        addNotification('OK', 'Local removido', 'success');
                      } catch (e) {
                        addNotification('Erro', e.message || 'Erro', 'error');
                      }
                    }}
                  >
                    Excluir local
                  </button>
                </div>
              </div>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 18, fontSize: 13 }}>
                {(v.rooms || []).map((r) => (
                  <li key={r.id} style={{ marginBottom: 4 }}>
                    {r.name}
                    {r.building ? ` (${r.building})` : ''}
                    {' — '}
                    até {r.capacity} candidato(s)
                    {r.notes ? ` — Obs.: ${r.notes}` : ''}{' '}
                    <button
                      type="button"
                      style={{ fontSize: 11, border: 'none', background: 'none', color: '#2563EB', cursor: 'pointer', padding: 0, marginRight: 8 }}
                      onClick={async () => {
                        const newName = (window.prompt('Nome ou número da sala', r.name || '') || '').trim();
                        if (!newName) return;
                        const newBuilding = (window.prompt('Prédio ou unidade (opcional)', r.building || '') || '').trim();
                        const newNotes = (window.prompt('Observação (opcional)', r.notes || '') || '').trim();
                        const rawCap = window.prompt('Capacidade da sala', String(r.capacity || 30));
                        if (rawCap == null) return;
                        const newCap = Math.max(1, parseInt(rawCap, 10) || 30);
                        try {
                          await apiClient.updateExamRoom(r.id, {
                            name: newName,
                            building: newBuilding || '',
                            notes: newNotes || '',
                            capacity: newCap,
                          });
                          await loadExamVenues(examVenuesModalProcess?.id);
                          addNotification('OK', 'Sala atualizada', 'success');
                        } catch (e) {
                          addNotification('Erro', e.message || 'Erro', 'error');
                        }
                      }}
                    >
                      editar sala
                    </button>
                    <button
                      type="button"
                      style={{ fontSize: 11, border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', padding: 0 }}
                      onClick={async () => {
                        try {
                          await apiClient.deleteExamRoom(r.id);
                          await loadExamVenues(examVenuesModalProcess?.id);
                        } catch (e) {
                          addNotification('Erro', e.message || 'Erro', 'error');
                        }
                      }}
                    >
                      excluir sala
                    </button>
                  </li>
                ))}
              </ul>
              {(!v.rooms || v.rooms.length === 0) && (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>Nenhuma sala neste local.</div>
              )}
            </div>
          ))}

          {!examVenuesLoading && (!examVenuesList || examVenuesList.length === 0) && (
            <p style={{ fontSize: 13, color: '#6B7280' }}>Nenhum local cadastrado ainda.</p>
          )}

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <button type="button" style={{ ...styles.button, ...styles.buttonSecondary }} onClick={() => setExamVenuesModalProcess(null)}>
              Fechar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!gabaritoModeloModalProcess}
        onClose={() => setGabaritoModeloModalProcess(null)}
        title={gabaritoModeloModalProcess ? `Gabarito da prova — ${gabaritoModeloModalProcess.name}` : ''}
        large
      >
        {gabaritoModeloModalProcess && (
          <div style={{ maxWidth: 960, maxHeight: '88vh', overflowY: 'auto' }}>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 12px 0', lineHeight: 1.55 }}>
              Defina as <strong>áreas ou disciplinas</strong> da prova objetiva, a <strong>quantidade de questões</strong> de cada uma e o <strong>peso</strong> (usado no cálculo escolhido em Processos → Prova objetiva). Esta configuração é <strong>única para o processo</strong> (vale para todos os cursos). Na folha PDF as questões são distribuídas em até 4 colunas; nomes de disciplina não aparecem na impressão.
            </p>
            <p style={{ fontSize: 13, color: '#047857', margin: '0 0 10px 0', lineHeight: 1.5 }}>
              Totais:{' '}
              <strong>
                {gabaritoExamRows.reduce((s, r) => s + (parseInt(String(r?.question_count ?? '').trim(), 10) || 0), 0)}
              </strong>{' '}
              questões · soma dos pesos{' '}
              <strong>
                {gabaritoExamRows.reduce((s, r) => s + (parseFloat(String(r?.weight ?? '').replace(',', '.'), 10) || 0), 0).toFixed(2)}
              </strong>
              <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#065F46' }}>
                O teto em pontos da parte objetiva no total da prova segue a <strong>soma das questões</strong> (salvar Gabarito
                atualiza o processo automaticamente). Os pesos entram no cálculo <strong>acertos × peso</strong> por área.
              </span>
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 14,
                alignItems: 'flex-end',
                marginBottom: 14,
                padding: '10px 12px',
                background: '#F0FDF4',
                borderRadius: 8,
                border: '1px solid #BBF7D0',
              }}
            >
              <div>
                <label style={{ ...styles.label, display: 'block', marginBottom: 4 }}>Alternativas por questão (folha OMR)</label>
                <select
                  value={gabaritoNumAlt}
                  onChange={(e) => setGabaritoNumAlt(e.target.value)}
                  style={{ ...styles.input, width: 72 }}
                >
                  {[2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={String(n)}>
                      {n} (A–{String.fromCharCode(64 + n)})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 220px' }}>
                <label style={{ ...styles.label, display: 'block', marginBottom: 4 }}>Tipos de folha / variantes (gabarito)</label>
                <input
                  type="text"
                  value={gabaritoVariantsStr}
                  onChange={(e) => setGabaritoVariantsStr(e.target.value)}
                  style={styles.input}
                  placeholder="Ex.: A ou A, B"
                />
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  Rótulos usados nas chaves (ex.: <code>A,B</code>). Na folha PDF aparece linha para marcar o tipo quando houver mais de um.
                </div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: '#1E40AF', margin: '0 0 12px 0', lineHeight: 1.5 }}>
              Salvo no sistema: preliminar{' '}
              <strong>{processPreliminaryAnswerKeyReady(gabaritoModeloModalProcess) ? 'completo' : 'incompleto ou ausente'}</strong>
              {' · '}definitivo{' '}
              <strong>{processDefinitiveAnswerKeyReady(gabaritoModeloModalProcess) ? 'completo' : 'incompleto ou ausente'}</strong>
              . A divulgação oficial do gabarito é feita pelos canais do edital (ex.: IFNMG / Editais em PDF). O{' '}
              <strong>definitivo salvo</strong> é obrigatório para leitura automática (OMR) e lançamento de notas objetivas; o{' '}
              <strong>preliminar salvo</strong> permite gerar o relatório 12; o recurso contra gabarito no portal segue apenas as datas do cronograma.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button
                type="button"
                onClick={() =>
                  setGabaritoExamRows((prev) => [...prev, { area: '', question_count: '', weight: 1 }])
                }
                style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
              >
                ➕ Linha
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#D1FAE5' }}>
                    <th style={{ ...styles.tableHeaderCell, textAlign: 'left', padding: '8px' }}>Disciplina / área</th>
                    <th style={{ ...styles.tableHeaderCell, textAlign: 'left', padding: '8px', width: 120 }}>Qtd. questões</th>
                    <th style={{ ...styles.tableHeaderCell, textAlign: 'left', padding: '8px', width: 100 }}>Peso</th>
                    <th style={{ ...styles.tableHeaderCell, padding: '8px', width: 72 }} />
                  </tr>
                </thead>
                <tbody>
                  {gabaritoExamRows.map((row, idx) => (
                    <tr key={idx} style={styles.tableRow}>
                      <td style={{ ...styles.tableCompactCell, padding: '8px' }}>
                        <input
                          type="text"
                          value={row.area || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setGabaritoExamRows((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], area: v };
                              return next;
                            });
                          }}
                          style={styles.input}
                          placeholder="Ex.: Português"
                        />
                      </td>
                      <td style={{ ...styles.tableCompactCell, padding: '8px' }}>
                        <input
                          type="number"
                          min={0}
                          value={row.question_count === '' ? '' : row.question_count}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setGabaritoExamRows((prev) => {
                              const next = [...prev];
                              next[idx] = {
                                ...next[idx],
                                question_count: raw === '' ? '' : parseInt(raw, 10) || 0,
                              };
                              return next;
                            });
                          }}
                          onWheel={(e) => e.preventDefault()}
                          style={styles.input}
                          placeholder="0"
                        />
                      </td>
                      <td style={{ ...styles.tableCompactCell, padding: '8px' }}>
                        <input
                          type="text"
                          value={row.weight === '' ? '' : row.weight}
                          onChange={(e) => {
                            const v0 = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                            const parts = v0.split('.');
                            const clean = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : v0;
                            setGabaritoExamRows((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], weight: clean === '' ? '' : clean };
                              return next;
                            });
                          }}
                          onWheel={(e) => e.preventDefault()}
                          style={styles.input}
                          placeholder="1"
                        />
                      </td>
                      <td style={{ ...styles.tableCompactCell, padding: '8px' }}>
                        <button
                          type="button"
                          onClick={() =>
                            setGabaritoExamRows((prev) => prev.filter((_, i) => i !== idx))
                          }
                          style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 style={{ margin: '18px 0 8px', fontSize: 14, color: '#111827' }}>Chaves oficiais (rascunho no servidor)</h4>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 10px 0', lineHeight: 1.5 }}>
              Marque uma resposta por questão na ordem da prova (1 a N). Os blocos seguem as disciplinas e quantidades cadastradas acima. Para cada tipo de folha ({gabaritoParsedLabels.join(', ')}) preencha{' '}
              <strong>todas</strong> as questões ou deixe <strong>todas</strong> em branco (—) naquela seção para não alterar o rascunho já salvo. Ao preencher o <strong>preliminar</strong>, a mesma letra é copiada para o <strong>definitivo</strong>; alterações só no definitivo não mudam o preliminar. A folha de respostas do candidato depende só das disciplinas e totais; as chaves ficam registradas aqui para recurso (preliminar) e correção (definitivo).
            </p>
            <div
              style={{
                fontSize: 11,
                color: '#475569',
                marginBottom: 10,
                padding: '8px 10px',
                background: '#F8FAFC',
                borderRadius: 6,
                border: '1px solid #E2E8F0',
                lineHeight: 1.45,
              }}
            >
              <strong>Símbolos especiais</strong> (além das alternativas A…):{' '}
              <strong style={{ fontFamily: 'monospace' }}>*</strong> — anulação com pontuação:{' '}
              <em>todos</em> recebem o ponto nesta questão;{' '}
              <strong style={{ fontFamily: 'monospace' }}>#</strong> — questão{' '}
              <em>não conta</em> na nota (sem acréscimo nem penalidade neste item). Disponíveis no preliminar e no definitivo para o admin registrar anulações antecipadas. A leitura/correção automática aplicará estas regras quando estiver integrada.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              {['preliminar', 'definitivo'].map((phase) => {
                const isPre = phase === 'preliminar';
                const grid = isPre ? gabaritoPrelimGrid : gabaritoDefGrid;
                const setGrid = isPre ? setGabaritoPrelimGrid : setGabaritoDefGrid;
                const letterOpts = gabaritoOmrLetterOptions(gabaritoNumAlt);
                const nq = gabaritoTotalQuestionsFromRows(gabaritoExamRows);
                const cellMin = nq >= 100 ? 96 : 84;
                return (
                  <div
                    key={phase}
                    style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 10 }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                      {isPre ? 'Preliminar' : 'Definitivo'}
                    </div>
                    {gabaritoParsedLabels.map((lab) => (
                      <div key={`${phase}-${lab}`} style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Tipo {lab}</div>
                        {nq <= 0 && (
                          <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>
                            Defina o total de questões nas disciplinas acima para habilitar a grade.
                          </p>
                        )}
                        {nq > 0 && gabaritoQuestionBlocks.length === 0 && (
                          <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>
                            Informe quantidades de questões por disciplina (maior que zero) nas linhas acima.
                          </p>
                        )}
                        {nq > 0 &&
                          gabaritoQuestionBlocks.map((blk) => (
                            <div key={`${phase}-${lab}-b-${blk.start}`} style={{ marginBottom: 10 }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: '#374151',
                                  marginBottom: 4,
                                }}
                              >
                                {blk.area}
                              </div>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: `repeat(auto-fill, minmax(${cellMin}px, 1fr))`,
                                  gap: '5px 8px',
                                  padding: 8,
                                  border: '1px solid #F3F4F6',
                                  borderRadius: 6,
                                  background: '#FAFAFA',
                                }}
                              >
                                {Array.from({ length: blk.count }, (_, j) => {
                                  const qn = blk.start + j;
                                  const idx = qn - 1;
                                  const val = (grid[lab] && grid[lab][idx]) || '';
                                  const boxW = val === '*' || val === '#' ? 30 : 26;
                                  return (
                                    <label
                                      key={qn}
                                      title={`Questão ${qn}${val === '*' ? ' — * todos pontuam' : val === '#' ? ' — # não conta na nota' : ''}`}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        cursor: 'pointer',
                                        fontSize: 11,
                                        lineHeight: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: '#6B7280',
                                          width: qn >= 100 ? 22 : 18,
                                          textAlign: 'right',
                                          flexShrink: 0,
                                          fontVariantNumeric: 'tabular-nums',
                                        }}
                                      >
                                        {qn}
                                      </span>
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '1px solid #94A3B8',
                                          borderRadius: 3,
                                          width: boxW,
                                          height: 22,
                                          flexShrink: 0,
                                          background: '#fff',
                                          boxSizing: 'border-box',
                                        }}
                                      >
                                        <select
                                          value={val}
                                          aria-label={`Questão ${qn}, tipo ${lab}`}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            if (isPre) {
                                              const nextPre = gabaritoGridWithCell(
                                                gabaritoPrelimGridRef.current,
                                                lab,
                                                idx,
                                                v,
                                                nq
                                              );
                                              gabaritoPrelimGridRef.current = nextPre;
                                              const nextDef = {
                                                ...gabaritoDefGridRef.current,
                                                [lab]: [...(nextPre[lab] || [])],
                                              };
                                              gabaritoDefGridRef.current = nextDef;
                                              setGabaritoPrelimGrid(nextPre);
                                              setGabaritoDefGrid(nextDef);
                                            } else {
                                              const nextDef = gabaritoGridWithCell(
                                                gabaritoDefGridRef.current,
                                                lab,
                                                idx,
                                                v,
                                                nq
                                              );
                                              gabaritoDefGridRef.current = nextDef;
                                              setGrid(nextDef);
                                            }
                                            setGabaritoDraftFeedback(null);
                                          }}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            border: 'none',
                                            background: 'transparent',
                                            padding: '0 2px',
                                            margin: 0,
                                            textAlign: 'center',
                                            textAlignLast: 'center',
                                            cursor: 'pointer',
                                            appearance: 'none',
                                            WebkitAppearance: 'none',
                                            MozAppearance: 'none',
                                            color: val ? '#111827' : '#9CA3AF',
                                          }}
                                        >
                                          <option value="">–</option>
                                          {letterOpts.map((L) => (
                                            <option key={L} value={L}>
                                              {L}
                                            </option>
                                          ))}
                                          {GABARITO_ANULACAO_SYMBOLS.map((sym) => (
                                            <option key={sym.value} value={sym.value} title={sym.title}>
                                              {sym.label}
                                            </option>
                                          ))}
                                        </select>
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {gabaritoHistory.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#111827' }}>Histórico recente</h4>
                <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        <th style={{ textAlign: 'left', padding: 6 }}>Quando</th>
                        <th style={{ textAlign: 'left', padding: 6 }}>Evento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gabaritoHistory.map((h) => (
                        <tr key={h.id} style={{ borderTop: '1px solid #E5E7EB' }}>
                          <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{h.created_at ? formatDate(h.created_at) : '—'}</td>
                          <td style={{ padding: 6 }}>
                            {h.event === 'publish_preliminary' && 'Publicação — preliminar'}
                            {h.event === 'publish_definitive' && 'Publicação — definitivo'}
                            {h.event === 'draft_preliminary' && 'Rascunho preliminar'}
                            {h.event === 'draft_definitive' && 'Rascunho definitivo'}
                            {h.event === 'config_exam' && 'Configuração'}
                            {!['publish_preliminary', 'publish_definitive', 'draft_preliminary', 'draft_definitive', 'config_exam'].includes(h.event) &&
                              (h.event || '—')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {gabaritoDraftFeedback && (
              <div
                role="status"
                style={{
                  marginTop: 16,
                  padding: '10px 14px',
                  borderRadius: 8,
                  fontSize: 13,
                  lineHeight: 1.45,
                  backgroundColor: gabaritoDraftFeedback.type === 'success' ? '#D1FAE5' : '#FEE2E2',
                  border: `1px solid ${gabaritoDraftFeedback.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
                  color: gabaritoDraftFeedback.type === 'success' ? '#065F46' : '#991B1B',
                }}
              >
                {gabaritoDraftFeedback.type === 'success' ? '✓ ' : '✕ '}
                {gabaritoDraftFeedback.text}
              </div>
            )}

            <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={() => {
                  setGabaritoModeloModalProcess(null);
                  setGabaritoDraftFeedback(null);
                }}
                disabled={gabaritoDraftSaving}
              >
                Fechar
              </button>
              <button
                type="button"
                style={{ ...styles.button, ...styles.buttonPrimary }}
                onClick={async () => {
                  const rows = (gabaritoExamRows || [])
                    .map((r) => ({
                      area: (r.area || '').trim(),
                      question_count: parseInt(String(r.question_count ?? '').trim(), 10) || 0,
                      weight: parseFloat(String(r.weight ?? '').replace(',', '.'), 10) || 0,
                    }))
                    .filter((r) => r.area);
                  const labels = parseGabaritoVariantLabels(gabaritoVariantsStr);
                  let nAlt = parseInt(String(gabaritoNumAlt || '5'), 10);
                  if (Number.isNaN(nAlt)) nAlt = 5;
                  nAlt = Math.max(2, Math.min(6, nAlt));
                  const nq = gabaritoTotalQuestionsFromRows(gabaritoExamRows);
                  const preStrings = labels.map((l) => gabaritoGridToFilledString(gabaritoPrelimGrid[l], nq));
                  const defStrings = labels.map((l) => gabaritoGridToFilledString(gabaritoDefGrid[l], nq));
                  const allPreF = nq > 0 && preStrings.every((s) => s !== null);
                  const allPreE =
                    nq === 0 ||
                    labels.every((l) => {
                      const row = gabaritoPrelimGrid[l] || [];
                      for (let i = 0; i < nq; i += 1) if (row[i]) return false;
                      return true;
                    });
                  const allDefF = nq > 0 && defStrings.every((s) => s !== null);
                  const allDefE =
                    nq === 0 ||
                    labels.every((l) => {
                      const row = gabaritoDefGrid[l] || [];
                      for (let i = 0; i < nq; i += 1) if (row[i]) return false;
                      return true;
                    });
                  const draftErr = (text) => {
                    setGabaritoDraftFeedback({ type: 'error', text });
                    addNotification('Erro', text, 'error');
                  };
                  if (!allPreE && !allPreF) {
                    draftErr(
                      'Preliminar: marque todas as questões em todos os tipos de folha ou deixe todas em branco (—) para manter o rascunho atual.'
                    );
                    return;
                  }
                  if (!allDefE && !allDefF) {
                    draftErr(
                      'Definitivo: marque todas as questões em todos os tipos ou deixe todas em branco (—) para manter o rascunho atual.'
                    );
                    return;
                  }
                  if (allPreF) {
                    const allowedChars = new Set([...'ABCDEF'.slice(0, nAlt), '*', '#']);
                    const lettersOk = (s) => [...String(s)].every((ch) => allowedChars.has(ch));
                    for (let i = 0; i < labels.length; i += 1) {
                      const s = preStrings[i];
                      if (!lettersOk(s)) {
                        draftErr(
                          `Preliminar tipo ${labels[i]}: use apenas A–${'ABCDEF'.slice(0, nAlt).slice(-1)}, * ou #.`
                        );
                        return;
                      }
                    }
                  }
                  if (allDefF) {
                    const allowedChars = new Set([...'ABCDEF'.slice(0, nAlt), '*', '#']);
                    const lettersOk = (s) => [...String(s)].every((ch) => allowedChars.has(ch));
                    for (let i = 0; i < labels.length; i += 1) {
                      const s = defStrings[i];
                      if (!lettersOk(s)) {
                        draftErr(
                          `Definitivo tipo ${labels[i]}: use apenas A–${'ABCDEF'.slice(0, nAlt).slice(-1)}, * ou #.`
                        );
                        return;
                      }
                    }
                  }
                  const payload = {
                    exam_area_weights: rows,
                    exam_num_alternatives: nAlt,
                    exam_key_variant_labels: labels,
                  };
                  if (allPreF) {
                    const preKeys = Object.fromEntries(
                      labels.map((l, i) => [l, preStrings[i]])
                    );
                    payload.exam_answer_key_preliminary = preKeys;
                    if (allDefE) {
                      payload.exam_answer_key_definitive = { ...preKeys };
                    }
                  }
                  if (allDefF) {
                    payload.exam_answer_key_definitive = Object.fromEntries(
                      labels.map((l, i) => [l, defStrings[i]])
                    );
                  }
                  setGabaritoDraftSaving(true);
                  setGabaritoDraftFeedback(null);
                  try {
                    const { error } = await apiClient.updateProcess(gabaritoModeloModalProcess.id, payload);
                    if (error) {
                      draftErr(error);
                      return;
                    }
                    const savedParts = ['estrutura da prova (disciplinas, alternativas e tipos de folha)'];
                    if (allPreF) savedParts.push('gabarito preliminar completo');
                    else if (allPreE) savedParts.push('preliminar mantido (sem alteração neste salvamento)');
                    if (allDefF) savedParts.push('gabarito definitivo completo');
                    else if (allDefE) savedParts.push('definitivo mantido (sem alteração neste salvamento)');
                    const okText = `Rascunho salvo no servidor: ${savedParts.join('; ')}.`;
                    setGabaritoDraftFeedback({ type: 'success', text: okText });
                    addNotification('Rascunho salvo', okText, 'success');
                    const arr = await loadProcesses();
                    const fresh = arr.find((x) => x.id === gabaritoModeloModalProcess.id);
                    if (fresh) setGabaritoModeloModalProcess(fresh);
                    try {
                      const hist = await apiClient.getProcessAnswerKeyHistory(gabaritoModeloModalProcess.id);
                      setGabaritoHistory(Array.isArray(hist) ? hist : []);
                    } catch (_) {
                      setGabaritoHistory([]);
                    }
                  } catch (e) {
                    draftErr(e.message || 'Erro ao salvar o rascunho.');
                  } finally {
                    setGabaritoDraftSaving(false);
                  }
                }}
                disabled={gabaritoDraftSaving}
              >
                {gabaritoDraftSaving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={essayLinesModalOpen}
        onClose={() => setEssayLinesModalOpen(false)}
        title="Folha de redação — número de linhas"
      >
        <div style={{ padding: '16px 20px 20px' }}>
          <p style={{ fontSize: 14, color: '#374151', margin: '0 0 12px', lineHeight: 1.5 }}>
            Informe quantas linhas pautadas a folha deve ter (entre 10 e 30). O espaçamento entre linhas é fixo em 7 mm
            (padrão de pauta de caderno), em uma página A4.
          </p>
          <label style={{ ...styles.label, display: 'block', marginBottom: 8 }}>Número de linhas</label>
          <input
            type="number"
            min={10}
            max={30}
            value={essayLinesDraft}
            onChange={(ev) => setEssayLinesDraft(ev.target.value)}
            style={{ ...styles.input, width: 120 }}
          />
          <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={() => setEssayLinesModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              style={styles.button}
              onClick={() => {
                const raw = parseInt(String(essayLinesDraft), 10);
                const n = Number.isFinite(raw) ? Math.max(10, Math.min(30, raw)) : 28;
                setFormData((prev) => ({ ...prev, exam_uses_essay: true, essay_sheet_num_lines: n }));
                setEssayLinesModalOpen(false);
              }}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>

      <ProcessQuotaConfigModal
        isOpen={!!quotaConfigProcess}
        onClose={() => setQuotaConfigProcess(null)}
        process={quotaConfigProcess}
        processes={processes}
        onSaved={async () => {
          const arr = await loadProcesses();
          if (quotaConfigProcess && arr.length) {
            const updated = arr.find(p => p.id === quotaConfigProcess.id);
            if (updated) setQuotaConfigProcess(updated);
          }
          addNotification('Sucesso', 'Configuração salva', 'success');
        }}
        onCopySuccess={() => addNotification('Sucesso', 'Modelo copiado. Clique em Salvar para gravar.', 'success')}
      />

      <Modal
        isOpen={!!docsModalProcess}
        onClose={() => { setDocsModalProcess(null); setDocFormError(''); }}
        title={docsModalProcess ? `Editais e anexos - ${docsModalProcess.name}` : ''}
        large
      >
        {docsModalProcess && (
          <>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const fileInput = e.target.querySelector('input[type="file"]');
                const file = fileInput?.files?.[0];
                if (!file) {
                  setDocFormError('Selecione um arquivo');
                  return;
                }
                const name = (docFormData.name || file.name || 'Documento').trim();
                setDocFormError('');
                setDocUploading(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  fd.append('name', name);
                  if (docFormData.published_at) fd.append('published_at', docFormData.published_at);
                  await apiClient.createProcessDocument(docsModalProcess.id, fd);
                  await loadProcessDocuments(docsModalProcess.id);
                  setDocFormData({ name: '', published_at: '' });
                  fileInput.value = '';
                  addNotification('Sucesso', 'Documento adicionado', 'success');
                } catch (err) {
                  setDocFormError(err.message || 'Erro ao enviar');
                } finally {
                  setDocUploading(false);
                }
              }}
              style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={styles.label}>Arquivo</label>
                  <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" style={styles.input} />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={styles.label}>Nome exibido</label>
                  <input
                    type="text"
                    value={docFormData.name}
                    onChange={(e) => setDocFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Ex: Edital 2026"
                    style={styles.input}
                  />
                </div>
                <div style={{ flex: '0 1 160px' }}>
                  <label style={styles.label}>Data publicação</label>
                  <input
                    type="date"
                    value={docFormData.published_at}
                    onChange={(e) => setDocFormData(p => ({ ...p, published_at: e.target.value }))}
                    style={styles.input}
                  />
                </div>
                <button type="submit" disabled={docUploading} style={{ ...styles.button, ...styles.buttonPrimary }}>
                  {docUploading ? 'Enviando...' : 'Adicionar'}
                </button>
              </div>
              {docFormError && (
                <div style={{ marginTop: '8px' }}>
                  <Alert message={docFormError} type="error" onClose={() => setDocFormError('')} />
                </div>
              )}
            </form>

            {loadingDocs ? (
              <LoadingSpinner />
            ) : processDocuments.length === 0 ? (
              <p style={{ color: '#6B7280', textAlign: 'center', padding: '24px' }}>Nenhum documento cadastrado.</p>
            ) : (
              <table style={{ ...styles.table, marginTop: '8px' }}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Nome</th>
                    <th style={styles.tableHeader}>Publicação</th>
                    <th style={styles.tableHeader}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {processDocuments.map((doc) => (
                    <tr key={doc.id}>
                      <td style={styles.tableCell}>{doc.name}</td>
                      <td style={styles.tableCell}>{doc.published_at ? formatDate(doc.published_at) : '-'}</td>
                      <td style={styles.tableCell}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const blob = await apiClient.downloadProcessDocument(doc.id);
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = doc.file_name || doc.name || 'documento';
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                addNotification('Erro', err.message || 'Falha ao baixar', 'error');
                              }
                            }}
                            style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                          >
                            ⬇️ Baixar
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Excluir este documento?')) return;
                              try {
                                await apiClient.deleteProcessDocument(doc.id);
                                await loadProcessDocuments(docsModalProcess.id);
                                addNotification('Sucesso', 'Documento excluído', 'success');
                              } catch (err) {
                                addNotification('Erro', err.message || 'Falha ao excluir', 'error');
                              }
                            }}
                            style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Modal>

      <Modal
        isOpen={!!scheduleModalProcess}
        onClose={() => { setScheduleModalProcess(null); setScheduleFormError(''); setEditingScheduleItem(null); setScheduleFormData({ label: '', date_type: 'period', single_date: '', start_date: '', end_date: '' }); }}
        title={scheduleModalProcess ? `Cronograma - ${scheduleModalProcess.name}` : ''}
        large
      >
        {scheduleModalProcess && (
          <>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
              Itens do sistema vêm das datas cadastradas no processo; ao alterar as datas aqui, o cadastro do processo é
              atualizado junto. Adicione etapas extras (manuais) abaixo.
            </p>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const isSystemEdit = editingScheduleItem && editingScheduleItem.source === 'system';
                const systemDateType = isSystemEdit ? (editingScheduleItem.date_type || 'period') : null;
                if (!isSystemEdit) {
                  const label = (scheduleFormData.label || '').trim();
                  if (!label) {
                    setScheduleFormError('Nome da etapa é obrigatório');
                    return;
                  }
                }
                const dateType = isSystemEdit ? systemDateType : (scheduleFormData.date_type || 'period');
                if (dateType === 'single' && !scheduleFormData.single_date) {
                  setScheduleFormError('Data única é obrigatória');
                  return;
                }
                if (dateType === 'period' && !scheduleFormData.start_date && !scheduleFormData.end_date) {
                  setScheduleFormError('Informe início ou fim do período');
                  return;
                }
                setScheduleFormError('');
                try {
                  if (isSystemEdit) {
                    const patchPayload = {
                      system_item_id: editingScheduleItem.id,
                      ...(systemDateType === 'single'
                        ? { single_date: scheduleFormData.single_date || '' }
                        : {
                            start_date: scheduleFormData.start_date || '',
                            end_date: scheduleFormData.end_date || '',
                          }),
                    };
                    const updated = await apiClient.updateProcessScheduleSystemItem(
                      scheduleModalProcess.id,
                      patchPayload
                    );
                    setProcesses((prev) => prev.map((pr) => (pr.id === updated.id ? { ...pr, ...updated } : pr)));
                    setScheduleModalProcess((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
                    if (editingProcess && editingProcess.id === updated.id) {
                      setFormData((prev) => ({
                        ...prev,
                        exam_card_release_start: updated.exam_card_release_start
                          ? String(updated.exam_card_release_start).slice(0, 10)
                          : '',
                        exam_card_release_end: updated.exam_card_release_end
                          ? String(updated.exam_card_release_end).slice(0, 10)
                          : '',
                        exam_day_date: updated.exam_day_date ? String(updated.exam_day_date).slice(0, 10) : '',
                      }));
                    }
                    addNotification('Sucesso', 'Datas atualizadas no processo', 'success');
                  } else {
                    const label = (scheduleFormData.label || '').trim();
                    const payload = {
                      label,
                      date_type: dateType,
                      single_date: dateType === 'single' ? scheduleFormData.single_date : undefined,
                      start_date: dateType === 'period' ? scheduleFormData.start_date || undefined : undefined,
                      end_date: dateType === 'period' ? scheduleFormData.end_date || undefined : undefined
                    };
                    if (editingScheduleItem) {
                      await apiClient.updateProcessScheduleItem(editingScheduleItem.id, payload);
                      addNotification('Sucesso', 'Etapa atualizada', 'success');
                    } else {
                      await apiClient.createProcessScheduleItem(scheduleModalProcess.id, payload);
                      addNotification('Sucesso', 'Etapa adicionada', 'success');
                    }
                  }
                  await loadProcessSchedule(scheduleModalProcess.id);
                  setScheduleFormData({ label: '', date_type: 'period', single_date: '', start_date: '', end_date: '' });
                  setEditingScheduleItem(null);
                } catch (err) {
                  setScheduleFormError(err.message || 'Erro ao salvar');
                }
              }}
              style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={styles.label}>Nome da etapa</label>
                  <input
                    type="text"
                    value={scheduleFormData.label}
                    onChange={(e) => setScheduleFormData(p => ({ ...p, label: e.target.value }))}
                    placeholder="Ex: Publicação do Edital"
                    readOnly={!!(editingScheduleItem && editingScheduleItem.source === 'system')}
                    style={{
                      ...styles.input,
                      ...(editingScheduleItem && editingScheduleItem.source === 'system'
                        ? { backgroundColor: '#F3F4F6', cursor: 'not-allowed' }
                        : {}),
                    }}
                  />
                </div>
                <div style={{ flex: '0 1 120px' }}>
                  <label style={styles.label}>Tipo</label>
                  <select
                    value={scheduleFormData.date_type}
                    onChange={(e) => setScheduleFormData(p => ({ ...p, date_type: e.target.value }))}
                    disabled={!!(editingScheduleItem && editingScheduleItem.source === 'system')}
                    style={styles.select}
                  >
                    <option value="period">Período</option>
                    <option value="single">Data única</option>
                  </select>
                </div>
                {(scheduleFormData.date_type === 'single' || (editingScheduleItem?.source === 'system' && editingScheduleItem?.date_type === 'single')) ? (
                  <div style={{ flex: '0 1 140px' }}>
                    <label style={styles.label}>Data</label>
                    <input
                      type="date"
                      value={scheduleFormData.single_date}
                      onChange={(e) => setScheduleFormData(p => ({ ...p, single_date: e.target.value }))}
                      style={styles.input}
                    />
                  </div>
                ) : (
                  <>
                    <div style={{ flex: '0 1 140px' }}>
                      <label style={styles.label}>Início</label>
                      <input
                        type="date"
                        value={scheduleFormData.start_date}
                        onChange={(e) => setScheduleFormData(p => ({ ...p, start_date: e.target.value }))}
                        style={styles.input}
                      />
                    </div>
                    <div style={{ flex: '0 1 140px' }}>
                      <label style={styles.label}>Fim</label>
                      <input
                        type="date"
                        value={scheduleFormData.end_date}
                        onChange={(e) => setScheduleFormData(p => ({ ...p, end_date: e.target.value }))}
                        style={styles.input}
                      />
                    </div>
                  </>
                )}
                <button type="submit" style={{ ...styles.button, ...styles.buttonPrimary }}>
                  {editingScheduleItem
                    ? (editingScheduleItem.source === 'system' ? 'Salvar datas' : 'Salvar')
                    : 'Adicionar'}
                </button>
                {editingScheduleItem && (
                  <button
                    type="button"
                    onClick={() => { setEditingScheduleItem(null); setScheduleFormData({ label: '', date_type: 'period', single_date: '', start_date: '', end_date: '' }); setScheduleFormError(''); }}
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
              {scheduleFormError && (
                <div style={{ marginTop: '8px' }}>
                  <Alert message={scheduleFormError} type="error" onClose={() => setScheduleFormError('')} />
                </div>
              )}
            </form>

            {loadingSchedule ? (
              <LoadingSpinner />
            ) : scheduleItems.length === 0 ? (
              <p style={{ color: '#6B7280', textAlign: 'center', padding: '24px' }}>Nenhum item no cronograma.</p>
            ) : (
              <table style={{ ...styles.table, marginTop: '8px' }}>
                <thead>
                  <tr>
                    <th style={styles.tableCompactHeaderCell}>Etapa</th>
                    <th style={styles.tableCompactHeaderCell}>Data(s)</th>
                    <th style={styles.tableCompactHeaderCell}>Status</th>
                    <th style={styles.tableCompactHeaderCell}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleItems.map((item) => {
                    const datesStr = item.date_type === 'single'
                      ? (item.single_date ? formatDate(item.single_date) : '-')
                      : [item.start_date, item.end_date].filter(Boolean).map(formatDate).join(' a ') || '-';
                    return (
                      <tr key={item.source === 'manual' ? item.id : item.id}>
                        <td style={styles.tableCompactCell}>
                          {item.label}
                          {item.source === 'system' && <span style={{ fontSize: '10px', color: '#9CA3AF', marginLeft: '4px' }}>(sistema)</span>}
                        </td>
                        <td style={styles.tableCompactCell}>{datesStr}</td>
                        <td style={styles.tableCompactCell}>
                          <span style={{
                            ...styles.statusBadge,
                            fontSize: '11px',
                            padding: '2px 6px',
                            ...(item.status === 'aberto' ? styles.statusApproved : item.status === 'nao_iniciado' ? { backgroundColor: '#FEF3C7', color: '#92400E' } : { backgroundColor: '#F3F4F6', color: '#6B7280' })
                          }}>
                            {item.status === 'aberto' ? 'Aberto' : item.status === 'nao_iniciado' ? 'Não iniciado' : 'Encerrado'}
                          </span>
                        </td>
                        <td style={styles.tableCompactCell}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {(item.source === 'manual' || item.source === 'system') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingScheduleItem(item);
                                  const d0 = item.start_date ? String(item.start_date).slice(0, 10) : '';
                                  const d1 = item.end_date ? String(item.end_date).slice(0, 10) : '';
                                  setScheduleFormData({
                                    label: item.label,
                                    date_type: item.date_type || 'period',
                                    single_date: item.single_date ? String(item.single_date).slice(0, 10) : '',
                                    start_date: d0,
                                    end_date: d1,
                                  });
                                  setScheduleFormError('');
                                }}
                                style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                                title="Editar"
                              >
                                ✏️
                              </button>
                            )}
                            {item.source === 'manual' && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Excluir esta etapa?')) return;
                                  try {
                                    await apiClient.deleteProcessScheduleItem(item.id);
                                    await loadProcessSchedule(scheduleModalProcess.id);
                                    if (editingScheduleItem?.id === item.id) {
                                      setEditingScheduleItem(null);
                                      setScheduleFormData({ label: '', date_type: 'period', single_date: '', start_date: '', end_date: '' });
                                    }
                                    addNotification('Sucesso', 'Etapa excluída', 'success');
                                  } catch (err) {
                                    addNotification('Erro', err.message || 'Falha ao excluir', 'error');
                                  }
                                }}
                                style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}
                                title="Excluir"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}); 